import type {AppMessagesManager, MyInputMessagesFilter, MyMessage, RequestHistoryOptions} from '@appManagers/appMessagesManager';
import {getOverlayRoot} from '@helpers/appWindow';
import appDialogsManager, {DIALOG_LIST_ELEMENT_TAG, DialogDom} from '@lib/appDialogsManager';
import {logger} from '@lib/logger';
import rootScope from '@lib/rootScope';
import {createSearchGroup, SearchGroup, SearchGroupType} from '@components/searchGroup';
import {horizontalMenu} from '@components/horizontalMenu';
import LazyLoadQueue from '@components/lazyLoadQueue';
import {putPreloader} from '@components/putPreloader';
import Scrollable from '@components/scrollable';
import {ScrollableContextValue} from '@components/scrollable2';
import useHeavyAnimationCheck, {getHeavyAnimationPromise} from '@hooks/useHeavyAnimationCheck';
import I18n, {LangPackKey, i18n, join} from '@lib/langPack';
import findUpClassName from '@helpers/dom/findUpClassName';
import {getMiddleware, Middleware, MiddlewareHelper} from '@helpers/middleware';
import {BotMenuButton, ChannelParticipant, Chat, ChatFull, ChatParticipant, ChatParticipants, Document, Message, MessageMedia, MessagesChats, MessagesFilter, Peer, Photo, StoryItem, Update, User, UserFull, WebPage} from '@layer';
import SortedUserList from '@components/sortedUserList';
import findUpTag from '@helpers/dom/findUpTag';
import appSidebarRight from '@components/sidebarRight';
import mediaSizes from '@helpers/mediaSizes';
import appImManager from '@lib/appImManager';
import IS_TOUCH_SUPPORTED from '@environment/touchSupport';
import handleTabSwipe from '@helpers/dom/handleTabSwipe';
import windowSize from '@helpers/windowSize';
import {formatPhoneNumber} from '@helpers/formatPhoneNumber';
import {ButtonMenuItemOptions, ButtonMenuSync} from '@components/buttonMenu';
import showForwardPopup from '@components/popups/forward';
import showDeleteMessagesPopup from '@components/popups/deleteMessages';
import {renderSearchWebPageRow} from '@components/searchWebPageRow';
import htmlToDocumentFragment from '@helpers/dom/htmlToDocumentFragment';
import {SearchSelection} from '@components/chat/selection';
import {attachClickEvent, simulateClickEvent} from '@helpers/dom/clickEvent';
import {MyDocument} from '@appManagers/appDocsManager';
import AppMediaViewer from '@components/mediaViewer';
import lockTouchScroll from '@helpers/dom/lockTouchScroll';
import copy from '@helpers/object/copy';
import safeAssign from '@helpers/object/safeAssign';
import ScrollableRefiller from '@helpers/scrollableRefiller';
import findAndSplice from '@helpers/array/findAndSplice';
import {ScrollStartCallbackDimensions} from '@helpers/fastSmoothScroll';
import setInnerHTML from '@helpers/dom/setInnerHTML';
import {AppManagers} from '@lib/managers';
import choosePhotoSize from '@appManagers/utils/photos/choosePhotoSize';
import wrapWebPageDescription from '@components/wrappers/webPageDescription';
import wrapWebPageTitle from '@components/wrappers/webPageTitle';
import wrapAbbreviation from '@richTextProcessor/wrapAbbreviation';
import matchUrl from '@richTextProcessor/matchUrl';
import wrapPlainText from '@richTextProcessor/wrapPlainText';
import wrapRichText from '@richTextProcessor/wrapRichText';
import wrapSenderToPeer from '@components/wrappers/senderToPeer';
import wrapSentTime from '@components/wrappers/sentTime';
import getMediaFromMessage from '@appManagers/utils/messages/getMediaFromMessage';
import filterMessagesByInputFilter from '@appManagers/utils/messages/filterMessagesByInputFilter';
import getChatMembersString from '@components/wrappers/getChatMembersString';
import getUserStatusString from '@components/wrappers/getUserStatusString';
import getParticipantPeerId from '@appManagers/utils/chats/getParticipantPeerId';
import {attachContextMenuListener} from '@helpers/dom/attachContextMenuListener';
import contextMenuController from '@helpers/contextMenuController';
import positionMenu from '@helpers/positionMenu';
import apiManagerProxy from '@lib/apiManagerProxy';
import ListenerSetter from '@helpers/listenerSetter';
import SwipeHandler from '@components/swipeHandler';
import wrapDocument from '@components/wrappers/document';
import wrapPhoto from '@components/wrappers/photo';
import wrapVideo from '@components/wrappers/video';
import wrapMediaSpoiler, {hasSensitiveSpoiler, onMediaSpoilerClick} from '@components/wrappers/mediaSpoiler';
import filterAsync from '@helpers/array/filterAsync';
import ChatContextMenu, {getSponsoredMessageButtons} from '@components/chat/contextMenu';
import getParticipantRank from '@appManagers/utils/chats/getParticipantRank';
import {NULL_PEER_ID} from '@appManagers/constants';
import createParticipantContextMenu from '@helpers/dom/createParticipantContextMenu';
import findAndSpliceAll from '@helpers/array/findAndSpliceAll';
import deferredPromise from '@helpers/cancellablePromise';
import {children, createEffect, createRoot, For, on} from 'solid-js';
import {StoriesProfileList} from '@components/stories/profileList';
import {StoriesContextActions} from '@components/stories/store';
import Button from '@components/button';
import anchorCallback from '@helpers/dom/anchorCallback';
import showPremiumPopup from '@components/popups/premium';
import {ChatType} from './chat/chatType';
import getFwdFromName from '@appManagers/utils/messages/getFwdFromName';
import SidebarSlider from '@components/slider';
import cancelClickOrNextIfNotClick from '@helpers/dom/cancelClickOrNextIfNotClick';
import createElementFromMarkup from '@helpers/createElementFromMarkup';
import numberThousandSplitter from '@helpers/number/numberThousandSplitter';
import {ALL_COLLECTIONS_ID, StarGiftsProfileActions, StarGiftsProfileStore} from '@components/stargifts/profileStore';
import {getFirstChild} from '@solid-primitives/refs';
import SortedDialogList from '@components/sortedDialogList';
import Icon from '@components/icon';
import {showAdReport} from '@components/popups/reportAd';
import ButtonMenuToggle from '@components/buttonMenuToggle';
import {isSensitive} from '@helpers/restrictions';
import {isMessageSensitive} from '@appManagers/utils/messages/isMessageRestricted';
import {MediaSearchContext} from '@components/appMediaPlaybackController';
import {StarGiftsProfileTab} from '@components/stargifts/profileList';
import {MyStarGift} from '@appManagers/appGiftsManager';
import wrapSticker from '@components/wrappers/sticker';
import {unwrap} from 'solid-js/store';
import {usePeer} from '@stores/peers';
import {useAppState} from '@stores/appState';
import {AutonomousSavedDialogList} from '@components/autonomousDialogList/savedDialogs';
import SetTransition from '@components/singleTransition';
import liteMode from '@helpers/liteMode';
import {wrapGlobalPostsSearch} from './sidebarLeft/globalPostsSearch';
import createMiddleware from '@helpers/solid/createMiddleware';
import Tabs from '@components/tabs';
import Section from '@components/section';
import copyMessageMediaWithFeedback from '@components/copyMessageMediaWithFeedback';
import createTopPeersList from '@components/topPeersList';
import {fastRaf} from '@helpers/schedulers';
import {SearchSuperMediaInputFilter} from '@components/sharedMediaFilters';
import {getMediaTypeForProfileTab, orderMediaTabsByMain, ProfileTabMediaType} from '@components/sharedMediaProfileTab';
import {supportsSharedMediaScrollDate} from '@components/sharedMediaScrollDate';
import createSharedMediaScrollDateBadge from '@components/sharedMediaScrollDateBadge';

// The call log is searched by the Calls tab, never by shared media — and unlike
// every other filter its constructor is not just its id (it carries `pFlags`),
// so a media tab could not build one from `{_: inputFilter}` anyway.
export type SearchSuperType = Exclude<MyInputMessagesFilter, 'inputMessagesFilterPhoneCalls'>/*  | 'members' */;
export type SearchSuperContext = {
  peerId: PeerId,
  inputFilter: {_: MyInputMessagesFilter},
  query?: string,
  maxId?: number,
  folderId?: number,
  threadId?: number,
  monoforumThreadId?: PeerId,
  date?: number,
  nextRate?: number,
  minDate?: number,
  maxDate?: number
} & Pick<RequestHistoryOptions, 'chatType' | 'communityId'>;

export type SearchSuperMediaType = 'stories' | 'members' | 'media' |
  'files' | 'links' | 'music' | 'chats' | 'voice' | 'groups' | 'similar' |
  'savedDialogs' | 'saved' | 'channels' | 'apps' | 'gifts' | 'posts';
export type SearchSuperMediaTab = {
  inputFilter?: SearchSuperType,
  name: LangPackKey,
  type: SearchSuperMediaType,
  contentTab?: HTMLElement,
  itemsTab?: HTMLElement,
  menuTab?: HTMLElement,
  menuTabName?: HTMLElement,
  scroll?: {scrollTop: number, scrollHeight: number},
  hideOn?: HTMLElement
};

export type SearchSuperMediaCounters = {
  photos: number,
  videos: number
};

// * a tab whose visibility follows its message counter — an empty one is hidden (see loadFirstTime)
export function isCounterDrivenMediaTab(mediaTab: SearchSuperMediaTab) {
  return !!mediaTab.inputFilter && mediaTab.inputFilter !== 'inputMessagesFilterEmpty';
}

type SearchSuperCommittedMediaFilterState = {
  inputFilter: SearchSuperMediaInputFilter,
  loaded: boolean,
  nextRate: number,
  counter: number,
  scroll: SearchSuperMediaTab['scroll']
};

type SearchSuperLoadTypeOptions = {
  mediaTab: SearchSuperMediaTab,
  justLoad: boolean,
  loadCount: number,
  middleware: Middleware,
  side: 'top' | 'bottom'
};

type PerformSearchResultArgs = {
  messages: (Message.message | Message.messageService)[];
  mediaTab: SearchSuperMediaTab;
  canAnimateIn?: boolean;
  append?: boolean;
};

class SearchContextMenu {
  private buttons: (ButtonMenuItemOptions & {verify?: () => boolean | Promise<boolean>, withSelection?: true})[];
  private element: HTMLElement;
  private target: HTMLElement;
  private peerId: PeerId;
  private mid: number;
  private isSelected: boolean;
  private managers: AppManagers;
  private noForwards: boolean;
  private message: MyMessage;
  private selectedMessages: MyMessage[];
  private copyMediaButton: ButtonMenuItemOptions & {
    verify?: () => boolean | Promise<boolean>
  };

  constructor(
    private attachTo: HTMLElement,
    private searchSuper: AppSearchSuper,
    private listenerSetter: ListenerSetter
  ) {
    this.managers = searchSuper.managers;

    const onContextMenu: Parameters<typeof attachContextMenuListener>[0]['callback'] = (e) => {
      if(this.init) {
        this.init();
        this.init = null;
      }

      let item: HTMLElement;
      try {
        item = findUpClassName(e.target, 'search-super-item');
      } catch(e) {}

      // stories have their own context menu in StoriesProfileTab
      const isStory = !!findUpClassName(e.target, 'search-super-content-stories');
      if(isStory) return;

      if(!item) return;

      // cross-realm-safe `instanceof MouseEvent` (excludes touch, survives the Document PiP window)
      if(!('touches' in e)) e.preventDefault();
      if(this.element.classList.contains('active')) {
        return false;
      }
      if(!('touches' in e)) e.cancelBubble = true;

      const r = async() => {
        this.target = item;
        this.peerId = item.dataset.peerId.toPeerId();
        this.mid = +item.dataset.mid;
        this.isSelected = searchSuper.selection.isMidSelected(this.peerId, this.mid);
        this.message = await this.managers.appMessagesManager.getMessageByPeer(this.peerId, this.mid);
        this.noForwards = searchSuper.selection.isSelecting ?
          this.searchSuper.selection.selectionForwardBtn.classList.contains('hide') :
          !(await this.managers.appMessagesManager.canForward(this.message));
        this.selectedMessages = searchSuper.selection.isSelecting ? await searchSuper.selection.getSelectedMessages() : undefined;
        // this.isSavedDialog = !!(searchSuper.searchContext.peerId === rootScope.myId && searchSuper.searchContext.threadId);

        const f = await Promise.all(this.buttons.map(async(button) => {
          let good: boolean;

          if(this.searchSuper.selection.isSelecting && !button.withSelection) {
            good = false;
          } else {
            good = button.verify ? !!(await button.verify()) : true;
          }

          button.element.classList.toggle('hide', !good);
          return good;
        }));

        if(!f.some((v) => v)) {
          return;
        }

        item.classList.add('menu-open');

        positionMenu(e, this.element);
        contextMenuController.openBtnMenu(this.element, () => {
          item.classList.remove('menu-open');
        });
      };

      r();
    };

    attachContextMenuListener({
      element: attachTo,
      callback: onContextMenu as any,
      listenerSetter
    });
  }

  private init() {
    this.buttons = [{
      icon: 'forward',
      text: 'Forward',
      onClick: this.onForwardClick,
      verify: () => !this.noForwards
    }, {
      icon: 'forward',
      text: 'Message.Context.Selection.Forward',
      onClick: this.onForwardClick,
      verify: () => this.searchSuper.selection.isSelecting && !this.noForwards,
      withSelection: true
    }, this.copyMediaButton = {
      icon: 'copy',
      text: 'MediaViewer.Context.Copy',
      onClick: this.onCopyMediaClick,
      verify: () => !this.searchSuper.selection.isSelecting &&
        ChatContextMenu.canCopyMedia(this.message, undefined, this.noForwards, this.attachTo),
      keepOpen: true
    }, {
      icon: 'download',
      text: 'MediaViewer.Context.Download',
      onClick: () => ChatContextMenu.onDownloadClick(this.message, this.noForwards, this.attachTo),
      verify: () => !this.searchSuper.selection.isSelecting && ChatContextMenu.canDownload(this.message, undefined, this.noForwards, this.attachTo)
    }, {
      icon: 'download',
      text: 'Message.Context.Selection.Download',
      onClick: () => ChatContextMenu.onDownloadClick(this.selectedMessages, this.noForwards, this.attachTo),
      verify: () => this.searchSuper.selection.isSelecting && ChatContextMenu.canDownload(this.selectedMessages, undefined, this.noForwards, this.attachTo),
      withSelection: true
    }, {
      icon: 'message',
      text: 'Message.Context.Goto',
      onClick: this.onGotoClick,
      withSelection: true
    }, {
      icon: 'select',
      text: 'Message.Context.Select',
      onClick: this.onSelectClick,
      verify: () => !this.isSelected,
      withSelection: true
    }, {
      icon: 'select',
      text: 'Message.Context.Selection.Clear',
      onClick: this.onClearSelectionClick,
      verify: () => this.isSelected,
      withSelection: true
    }, {
      icon: 'delete',
      className: 'danger',
      text: 'Delete',
      onClick: this.onDeleteClick,
      verify: () => !this.searchSuper.selection.isSelecting && this.managers.appMessagesManager.canDeleteMessage(this.message)
    }, {
      icon: 'delete',
      className: 'danger',
      text: 'Message.Context.Selection.Delete',
      onClick: this.onDeleteClick,
      verify: () => this.searchSuper.selection.isSelecting && this.searchSuper.selection.selectionDeleteBtn && !this.searchSuper.selection.selectionDeleteBtn.classList.contains('hide'),
      withSelection: true
    }];

    this.element = ButtonMenuSync({buttons: this.buttons, listenerSetter: this.listenerSetter});
    this.element.classList.add('search-contextmenu', 'contextmenu');
    getOverlayRoot().append(this.element);
  }

  private onGotoClick = () => {
    appImManager.setInnerPeer({
      peerId: this.peerId,
      lastMsgId: this.mid,
      threadId: this.searchSuper.mediaTab.type === 'saved' ? this.searchSuper.searchContext.peerId : this.searchSuper.searchContext.threadId
    });
  };

  private onCopyMediaClick = () => {
    copyMessageMediaWithFeedback({
      message: this.message,
      button: this.copyMediaButton
    });
  };

  private onForwardClick = () => {
    if(this.searchSuper.selection.isSelecting) {
      simulateClickEvent(this.searchSuper.selection.selectionForwardBtn);
    } else {
      showForwardPopup({
        [this.peerId]: [this.mid]
      });
    }
  };

  private onSelectClick = () => {
    this.searchSuper.selection.toggleByElement(this.target);
  };

  private onClearSelectionClick = () => {
    this.searchSuper.selection.cancelSelection();
  };

  private onDeleteClick = () => {
    if(this.searchSuper.selection.isSelecting) {
      simulateClickEvent(this.searchSuper.selection.selectionDeleteBtn);
    } else {
      showDeleteMessagesPopup(
        this.peerId,
        [this.mid],
        ChatType.Chat
      );
    }
  };
}

export type ProcessSearchSuperResult = {
  message: Message.message,
  middleware: Middleware,
  promises: Promise<any>[],
  elemsToAppend: {element: HTMLElement, message: any}[],
  inputFilter: MyInputMessagesFilter,
  searchGroup?: SearchGroup,
  mediaTab: SearchSuperMediaTab
};

export default class AppSearchSuper {
  public tabs: {[t in SearchSuperType]: HTMLDivElement} = {} as any;

  public mediaTab: SearchSuperMediaTab;

  public container: HTMLElement;
  public nav: HTMLElement;
  public navScrollableContainer: HTMLDivElement;
  public tabsContainer: HTMLElement;
  private disposeNav: () => void;
  private tabsMenu: HTMLElement;
  private prevTabId = -1;

  private lazyLoadQueue = new LazyLoadQueue();
  public middleware = getMiddleware();

  public historyStorage: Partial<{[type in SearchSuperType]: {mid: number, peerId: PeerId}[]}> = {};
  public usedFromHistory: Partial<{[type in SearchSuperType]: number}> = {};

  public searchContext: SearchSuperContext;
  public loadMutex: Promise<any>;

  private nextRates: Partial<{[type in SearchSuperMediaType]: number}> = {};
  private loadPromises: Partial<{[type in SearchSuperMediaType]: Promise<any>}> = {};
  private loaded: Partial<{[type in SearchSuperMediaType]: boolean}> = {};
  private refiller: ScrollableRefiller<SearchSuperMediaType>;
  private loadedChats = false;
  private firstLoad = true;

  private log = logger('SEARCH-SUPER');
  public selectTab: ReturnType<typeof horizontalMenu>;


  private searchGroupMedia: SearchGroup;

  public mediaTabsMap: Map<SearchSuperMediaType, SearchSuperMediaTab> = new Map();

  private membersList: SortedUserList;
  private membersParticipantMap: Map<PeerId, ChatParticipant | ChannelParticipant>;
  private membersMiddlewareHelper: MiddlewareHelper;

  public storiesActions: StoriesContextActions;
  public storiesSetAlbum: (albumId: number | undefined, skipAnimation?: boolean) => void;
  private _loadSavedDialogs: (side: 'top' | 'bottom') => Promise<any>;

  private _loadMoreApps: () => Promise<void>;

  private skipScroll: boolean;

  // * arguments
  public mediaTabs: SearchSuperMediaTab[];
  private mediaTabsDefaultOrder: SearchSuperMediaTab[];
  public mainMediaTabType: ProfileTabMediaType;
  public scrollable: Scrollable;
  public searchGroups?: {[group in SearchGroupType]: SearchGroup};
  public asChatList? = false;
  public hideEmptyTabs? = true;
  public onChangeTab?: (mediaTab: SearchSuperMediaTab) => void;
  public showSender? = false;
  public useMainProfileTab?: boolean;

  private searchContextMenu: SearchContextMenu;
  public selection: SearchSelection;

  public scrollStartCallback: (dimensions: ScrollStartCallbackDimensions) => void;
  public scrollOffset: number;

  public managers: AppManagers;
  private loadFirstTimePromise: Promise<void>;
  private mediaFilterChangeId = 0;
  private mediaFilterCommittedInputFilter: SearchSuperMediaInputFilter;
  private mediaFilterCommittedState: SearchSuperCommittedMediaFilterState;
  private mediaFilterStagingItemsTab: HTMLDivElement;
  private mediaCountersRefreshId = 0;
  private mediaCountersVersion = 0;
  private scrollDateBadge: ReturnType<typeof createSharedMediaScrollDateBadge>;

  private listenerSetter: ListenerSetter;
  private swipeHandler: SwipeHandler;

  public storiesArchive: boolean;

  public counters: Partial<{[type in SearchSuperMediaType]: number}> = {};
  public onLengthChange: (type: SearchSuperMediaType, length: number) => void;
  public onMediaCountersChange: (counters?: SearchSuperMediaCounters) => void;
  private mediaCounters: Partial<SearchSuperMediaCounters> = {};

  public openSavedDialogsInner: boolean;

  public slider: SidebarSlider;

  public stargiftsStore: StarGiftsProfileStore;
  public stargiftsActions: StarGiftsProfileActions;
  public stargiftsSetCollection: (collectionId: number) => void;
  private menuGradient: HTMLElement;

  constructor(options: Pick<
    AppSearchSuper,
    'mediaTabs' |
    'scrollable' |
    'searchGroups' |
    'asChatList' |
    'hideEmptyTabs' |
    'onChangeTab' |
    'showSender' |
    'managers' |
    'scrollOffset'
  > & Partial<Pick<AppSearchSuper, 'storiesArchive' | 'onLengthChange' | 'onMediaCountersChange' | 'openSavedDialogsInner' | 'slider' | 'useMainProfileTab'>>) {
    safeAssign(this, options);
    this.mediaTabsDefaultOrder = [...this.mediaTabs];

    this.refiller = new ScrollableRefiller({
      scrollable: this.scrollable,
      getProgress: (type) => this.getMediaTabProgress(type)
    });

    this.slider ??= appSidebarRight;

    this.container = document.createElement('div');
    this.container.classList.add('search-super');

    this.listenerSetter = new ListenerSetter();
    this.searchContextMenu = new SearchContextMenu(this.container, this, this.listenerSetter);
    this.selection = new SearchSelection(this, this.managers, this.listenerSetter);

    let navScrollableContext: ScrollableContextValue;
    // the tabs are reordered and hidden by hand later on, so they are rendered once and
    // kept as plain elements — no `For`, nothing reactive to fight with
    const navScrollableContainer = this.navScrollableContainer = createRoot((dispose) => {
      this.disposeNav = dispose;
      return Tabs.MenuScrollable({
        class: 'search-super-tabs-scrollable sticky',
        scrollableProps: {
          class: 'search-super-nav-scrollable',
          contextRef: (context) => navScrollableContext = context
        },
        children: Tabs.Menu({
          class: 'search-super-tabs',
          ref: (ref) => this.tabsMenu = this.nav = ref,
          children: this.mediaTabs.map((mediaTab) => {
            this.mediaTabsMap.set(mediaTab.type, mediaTab);
            return Tabs.MenuTab({
              ripple: true,
              ref: (ref) => mediaTab.menuTab = ref,
              children: (mediaTab.menuTabName = i18n(mediaTab.name))
            });
          })
        })
      });
    }) as HTMLDivElement;

    this.tabsContainer = document.createElement('div');
    this.tabsContainer.classList.add('search-super-tabs-container', 'tabs-container');

    let unlockScroll: ReturnType<typeof lockTouchScroll>;
    if(IS_TOUCH_SUPPORTED) {
      this.swipeHandler = handleTabSwipe({
        element: this.tabsContainer,
        onSwipe: (xDiff, yDiff, e) => {
          xDiff *= -1;
          yDiff *= -1;
          const prevId = this.selectTab.prevId();
          const children = Array.from(this.tabsMenu.children) as HTMLElement[];

          const prevChild = this.mediaTabs[prevId];
          if(prevChild.type === 'gifts' && this.stargiftsActions?.handleSwipe(xDiff, this.stargiftsSetCollection)) {
            return
          }

          if(prevChild.type === 'stories' && this.storiesActions?.handleSwipe(xDiff, this.storiesSetAlbum)) {
            return
          }

          let idx: number;
          if(xDiff > 0) {
            for(let i = prevId + 1; i < children.length; ++i) {
              if(!children[i].classList.contains('hide')) {
                idx = i;
                break;
              }
            }
          } else {
            for(let i = prevId - 1; i >= 0; --i) {
              if(!children[i].classList.contains('hide')) {
                idx = i;
                break;
              }
            }
          }

          if(idx !== undefined) {
            unlockScroll = lockTouchScroll(this.tabsContainer);
            this.selectTab(idx);
          }
        },
        verifyTouchTarget: (e) => {
          return !findUpClassName(e.target, 'scrollable-x');
        }
      });
    }

    const noSectionTypes: Set<SearchSuperMediaType> = new Set([
      'stories',
      'media',
      'gifts',
      'chats',
      'channels',
      'apps',
      'posts'
    ]);
    for(const mediaTab of this.mediaTabs) {
      const container = document.createElement('div');
      container.classList.add('search-super-tab-container', 'search-super-container-' + mediaTab.type, 'tabs-tab');

      const content = document.createElement('div');
      content.classList.add('search-super-content-container', 'search-super-content-' + mediaTab.type);

      container.append(content);

      const useSection = !noSectionTypes.has(mediaTab.type);
      let itemsContainer = content;
      if(useSection) {
        const items = document.createElement('div');
        createRoot(() => {
          Section({
            noDelimiter: true,
            class: 'hide',
            ref: (ref) => {
              content.append(mediaTab.hideOn = ref);
            },
            children: [items]
          });
        });
        itemsContainer = items;
      } else if(mediaTab.type === 'media') {
        const grid = document.createElement('div');
        grid.classList.add('search-super-content-media-grid');
        content.append(grid);
        itemsContainer = grid;
      }

      this.tabsContainer.append(container);

      const {inputFilter} = mediaTab;
      if(inputFilter) {
        this.tabs[inputFilter] = itemsContainer;
      }

      mediaTab.contentTab = content;
      mediaTab.itemsTab = itemsContainer;
    }

    this.container.append(
      this.menuGradient = Tabs.MenuGradient({
        color: 'background',
        className: 'search-super-tabs-gradient'
      }) as HTMLElement,
      navScrollableContainer,
      this.tabsContainer
    );

    // * construct end

    this.searchGroupMedia = createSearchGroup({type: 'messages'});

    // this.scrollable.onScrolledTop = () => {
    //   if(this.mediaTab.contentTab && this.canLoadMediaTab(this.mediaTab)/* && false */) {
    //     // this.log('onScrolledTop will load media');
    //     this.load(true, undefined, 'top');
    //   }
    // };

    this.scrollable.onScrolledBottom = () => {
      if(this.mediaTab.contentTab && this.canLoadMediaTab(this.mediaTab)/* && false */) {
        // this.log('onScrolledBottom will load media');
        this.load(true, undefined, 'bottom');
      }
    };
    // this.scroll.attachSentinels(undefined, 400);

    this.selectTab = horizontalMenu(this.tabsMenu, this.tabsContainer, (id, tabContent, animate) => {
      if(this.prevTabId === id && !this.skipScroll) {
        this.scrollToStart();
        return;
      }

      const newMediaTab = this.mediaTabs[id];
      this.onChangeTab?.(newMediaTab);


      const fromMediaTab = this.mediaTab;
      this.mediaTab = newMediaTab;

      if(!supportsSharedMediaScrollDate(newMediaTab.type)) {
        this.scrollDateBadge?.hide();
      }

      if(this.prevTabId !== -1 && animate) {
        this.onTransitionStart();
      }

      if(this.skipScroll) {
        this.skipScroll = false;
      } else {
        const offsetTop = this.container.offsetTop - (this.scrollOffset || 0);
        let scrollTop = this.scrollable.scrollPosition;
        if(scrollTop < offsetTop) {
          this.scrollToStart();
          scrollTop = offsetTop;
        }

        fromMediaTab.scroll = {scrollTop: scrollTop, scrollHeight: this.scrollable.scrollSize};

        if(newMediaTab.scroll === undefined) {
          const rect = this.container.getBoundingClientRect();
          const rect2 = this.container.parentElement.getBoundingClientRect();
          const diff = rect.y - rect2.y;

          if(scrollTop > diff) {
            newMediaTab.scroll = {scrollTop: diff, scrollHeight: 0};
          }
        }

        if(newMediaTab.scroll) {
          const diff = fromMediaTab.scroll.scrollTop - newMediaTab.scroll.scrollTop;
          // console.log('what you gonna do', this.goingHard, diff);

          // this.scrollable.scrollTop = scrollTop;
          if(diff/*  && diff < 0 */) {
            /* if(diff > -(fromMediaTab.contentTab.scrollHeight + this.nav.scrollHeight)) {
              fromMediaTab.contentTab.style.transform = `translateY(${diff}px)`;
              this.scrollable.scrollTop = scrollTop - diff;
            } else { */
            newMediaTab.contentTab.style.transform = `translateY(${diff}px)`;
            // }
          }
        }
      }

      /* if(this.prevTabId !== -1 && nav.offsetTop) {
        this.scrollable.scrollTop -= nav.offsetTop;
      } */

      /* this.log('setVirtualContainer', id, this.sharedMediaSelected, this.sharedMediaSelected.childElementCount);
      this.scroll.setVirtualContainer(this.sharedMediaSelected); */

      if(this.prevTabId !== -1 && !newMediaTab.itemsTab.childElementCount) { // quick brown fix
        // this.contentContainer.classList.remove('loaded');
        this.load(true);
      }

      this.prevTabId = id;
    }, () => {
      this.scrollable.onScroll();

      // console.log('what y', this.tabSelected.style.transform);
      if(this.mediaTab.scroll !== undefined) {
        this.mediaTab.contentTab.style.transform = '';
        this.scrollable.scrollPosition = this.mediaTab.scroll.scrollTop;
      }

      if(unlockScroll) {
        unlockScroll();
        unlockScroll = undefined;
      }

      this.onTransitionEnd();
    }, undefined, navScrollableContext, this.listenerSetter);

    attachClickEvent(this.tabsContainer, (e) => {
      if(this.selection.isSelecting) {
        cancelClickOrNextIfNotClick(e);
        this.selection.toggleByElement(findUpClassName(e.target, 'search-super-item'));
      }
    }, {capture: true, passive: false, listenerSetter: this.listenerSetter});

    const onMediaClick = async(className: string, targetClassName: string, inputFilter: SearchSuperType, e: MouseEvent) => {
      const target = findUpClassName(e.target as HTMLDivElement, className);
      if(!target) return;

      const mid = +target.dataset.mid;
      if(!mid) {
        this.log.warn('no messageId by click on target:', target);
        return;
      }

      const mediaSpoiler: HTMLElement = target.querySelector('.media-spoiler-container');
      if(mediaSpoiler) {
        onMediaSpoilerClick({
          event: e,
          mediaSpoiler
        });
        return;
      }

      const peerId = target.dataset.peerId.toPeerId();
      const message = await this.managers.appMessagesManager.getMessageByPeer(peerId, mid);
      if(!target.isConnected) {
        return;
      }

      const skipSensitive = this.isMessageSensitive(message as Message.message);

      const targets = (Array.from(this.tabs[inputFilter].querySelectorAll('.' + targetClassName)) as HTMLElement[]).map((el) => {
        const containerEl = findUpClassName(el, className);
        if(skipSensitive && hasSensitiveSpoiler(containerEl)) {
          return;
        }

        return {
          element: el,
          mid: +containerEl.dataset.mid,
          peerId: containerEl.dataset.peerId.toPeerId()
        };
      }).filter(Boolean);

      // const ids = Object.keys(this.mediaDivsByIds).map((k) => +k).sort((a, b) => a - b);
      const idx = targets.findIndex((item) => item.mid === mid && item.peerId === peerId);

      const mediaTab = this.mediaTabs.find((mediaTab) => mediaTab.inputFilter === inputFilter);
      new AppMediaViewer()
      .setSearchContext(this.copySearchContext(inputFilter, this.nextRates[mediaTab.type], skipSensitive))
      .openMedia({
        message,
        target: targets[idx].element,
        fromRight: 0,
        reverse: false,
        prevTargets: targets.slice(0, idx),
        nextTargets: targets.slice(idx + 1)
      });
    };

    const mediaTab = this.mediaTabsMap.get('media');
    mediaTab?.itemsTab && attachClickEvent(
      mediaTab.itemsTab,
      (e) => {
        if(this.mediaFilterStagingItemsTab) return;
        return onMediaClick('grid-item', 'grid-item', mediaTab.inputFilter, e);
      },
      {listenerSetter: this.listenerSetter}
    );
    this.tabs.inputMessagesFilterDocument && attachClickEvent(
      this.tabs.inputMessagesFilterDocument,
      onMediaClick.bind(null, 'document-with-thumb', 'media-container', 'inputMessagesFilterDocument'),
      {listenerSetter: this.listenerSetter}
    );

    /* attachClickEvent(this.tabs.inputMessagesFilterUrl, (e) => {
      const target = e.target as HTMLElement;
      if(target.tagName === 'A') {
        return;
      }

      try {
        const a = findUpClassName(target, 'row').querySelector('.anchor-url:last-child') as HTMLAnchorElement;
        a.click();
      } catch(err) {}
    }); */

    this.mediaTab = this.mediaTabs[0];

    useHeavyAnimationCheck(() => {
      this.lazyLoadQueue.lock();
    }, () => {
      this.lazyLoadQueue.unlockAndRefresh(); // ! maybe not so efficient
    }, this.listenerSetter);
  }

  private scrollToStart() {
    this.scrollable.scrollIntoViewNew({
      element: this.container,
      position: 'start',
      startCallback: this.scrollStartCallback,
      getElementPosition: this.scrollOffset ? ({elementPosition}) => elementPosition - this.scrollOffset : undefined
    });
  }

  private onTransitionStart = () => {
    this.container.classList.add('sliding');
  };

  private onTransitionEnd = () => {
    this.container.classList.remove('sliding');
  };

  public getFirstVisibleMediaTab() {
    return this.mediaTabs.find((mediaTab) => !mediaTab.menuTab.classList.contains('hide'));
  }

  public setMainMediaTab(type?: ProfileTabMediaType) {
    this.mainMediaTabType = type;
    const ordered = orderMediaTabsByMain(
      this.mediaTabsDefaultOrder,
      type,
      (mediaTab) => !mediaTab.menuTab.classList.contains('hide')
    );
    if(ordered.every((mediaTab, index) => this.mediaTabs[index] === mediaTab)) {
      return;
    }

    this.mediaTabs.splice(0, this.mediaTabs.length, ...ordered);
    this.nav.append(...ordered.map((mediaTab) => mediaTab.menuTab));
    this.tabsContainer.append(...ordered.map((mediaTab) => mediaTab.contentTab.parentElement));

    if(this.prevTabId !== -1) {
      this.prevTabId = this.mediaTabs.indexOf(this.mediaTab);
    }
  }

  public setCounter(type: SearchSuperMediaType, count: number) {
    this.counters[type] = count;

    if(type === 'media') {
      const inputFilter = this.mediaTabsMap.get('media')?.inputFilter;
      if(inputFilter === 'inputMessagesFilterPhotos') {
        this.setMediaCounters({photos: count});
      } else if(inputFilter === 'inputMessagesFilterVideo') {
        this.setMediaCounters({videos: count});
      }
    }

    this.updateMediaTabVisibility(type);
    this.onLengthChange?.(type, count);
  }

  // * counter-driven tabs are hidden while empty (see loadFirstTime), so they have to appear
  // * (and disappear) on the fly when their counter crosses zero
  private updateMediaTabVisibility(type: SearchSuperMediaType) {
    if(!this.hideEmptyTabs || this.firstLoad) {
      return;
    }

    const mediaTab = this.mediaTabsMap.get(type);
    if(!mediaTab || !isCounterDrivenMediaTab(mediaTab)) {
      return;
    }

    const hide = !this.counters[type];
    if(mediaTab.menuTab.classList.contains('hide') === hide) {
      return;
    }

    mediaTab.menuTab.classList.toggle('hide', hide);

    let needChangeActive: boolean;
    if(hide) {
      needChangeActive = mediaTab.menuTab.classList.contains('active');
      mediaTab.menuTab.classList.remove('active');
    } else {
      // * there was nothing to select when every tab was empty
      needChangeActive = !this.mediaTabs.some((tab) => tab.menuTab.classList.contains('active'));
    }

    this.updateContainerHidden(needChangeActive);

    if(
      needChangeActive &&
      this.mediaTab &&
      !this.mediaTab.menuTab.classList.contains('hide') &&
      this.canLoadMediaTab(this.mediaTab)
    ) {
      this.load(true);
    }
  }

  private setMediaCounters(counters: Partial<SearchSuperMediaCounters>) {
    Object.assign(this.mediaCounters, counters);
    ++this.mediaCountersVersion;
    const {photos, videos} = this.mediaCounters;
    if(photos === undefined || videos === undefined) {
      return;
    }

    this.onMediaCountersChange?.({photos, videos});
  }

  public updateMediaCountersByMessage(message: MyMessage, difference: 1 | -1) {
    const key = this.filterMessagesByType([message], 'inputMessagesFilterPhotos').length ?
      'photos' :
      this.filterMessagesByType([message], 'inputMessagesFilterVideo').length ? 'videos' : undefined;
    if(!key || this.mediaCounters[key] === undefined) {
      return;
    }

    this.setMediaCounters({
      [key]: Math.max(0, this.mediaCounters[key] + difference)
    });
  }

  public async refreshMediaCounters() {
    const refreshId = ++this.mediaCountersRefreshId;
    const version = this.mediaCountersVersion;
    const middleware = this.middleware.get();

    try {
      const counters = await this.getSearchCounters([{
        _: 'inputMessagesFilterPhotos'
      }, {
        _: 'inputMessagesFilterVideo'
      }], false);
      if(
        refreshId !== this.mediaCountersRefreshId ||
        version !== this.mediaCountersVersion ||
        !middleware()
      ) {
        return;
      }

      const photos = counters.find((counter) => counter.filter._ === 'inputMessagesFilterPhotos')?.count;
      const videos = counters.find((counter) => counter.filter._ === 'inputMessagesFilterVideo')?.count;
      this.setMediaCounters({photos, videos});
    } catch(err) {
      this.log.error('refresh media counters error:', err);
    }
  }

  public async setMediaInputFilter(inputFilter: SearchSuperMediaInputFilter): Promise<SearchSuperMediaInputFilter | void> {
    const changeId = ++this.mediaFilterChangeId;
    const wasChanging = !!this.mediaFilterCommittedState;
    await this.loadPromises.media;

    const mediaTab = this.mediaTabsMap.get('media');
    if(!mediaTab || changeId !== this.mediaFilterChangeId) {
      return;
    }

    const committedInputFilter = this.mediaFilterCommittedInputFilter ??= mediaTab.inputFilter as SearchSuperMediaInputFilter;
    if(!wasChanging && committedInputFilter === inputFilter) {
      return;
    }

    const itemsTab = mediaTab.itemsTab as HTMLDivElement;
    this.mediaFilterCommittedState ??= {
      inputFilter: committedInputFilter,
      loaded: this.loaded.media,
      nextRate: this.nextRates.media,
      counter: this.counters.media,
      scroll: mediaTab.scroll
    };

    if(inputFilter === committedInputFilter || this.mediaTab !== mediaTab) {
      this.restoreCommittedMediaFilterState(mediaTab, itemsTab);
      return committedInputFilter;
    }

    const stagingItemsTab = itemsTab.cloneNode(false) as HTMLDivElement;
    this.mediaFilterStagingItemsTab = stagingItemsTab;
    mediaTab.inputFilter = inputFilter;
    this.tabs[inputFilter] = stagingItemsTab;
    this.searchContext.inputFilter = {_: inputFilter};
    this.historyStorage[inputFilter] = [];
    this.usedFromHistory[inputFilter] = -1;
    // * the counters the refiller watches just went back to the start, and it
    // * only re-arms on a HIGHER number — without this the media tab would never
    // * refill again for the rest of this peer
    this.refiller.reset('media');
    this.loaded.media = false;
    this.nextRates.media = 0;
    delete this.counters.media;

    mediaTab.scroll = undefined;

    let loadResult: Awaited<ReturnType<AppSearchSuper['load']>>;
    try {
      loadResult = await this.load(true);
    } catch(err) {
      this.log.error('media filter load error:', err);
    }

    if(changeId !== this.mediaFilterChangeId) {
      this.discardMediaFilterStagingItemsTab(stagingItemsTab);
      return;
    }

    if(!Array.isArray(loadResult) || typeof loadResult[0] !== 'number') {
      this.discardMediaFilterStagingItemsTab(stagingItemsTab);
      this.restoreCommittedMediaFilterState(mediaTab, itemsTab);
      return committedInputFilter;
    }

    await new Promise<void>((resolve) => fastRaf(() => {
      if(changeId !== this.mediaFilterChangeId) {
        this.discardMediaFilterStagingItemsTab(stagingItemsTab);
        resolve();
        return;
      }

      this.removeFromLazyLoadQueue(itemsTab);
      itemsTab.replaceChildren(...Array.from(stagingItemsTab.children));
      this.tabs[inputFilter] = itemsTab;
      this.mediaFilterCommittedInputFilter = inputFilter;
      this.mediaFilterCommittedState = undefined;

      const parent = mediaTab.contentTab.parentElement;
      Array.from(parent.children).slice(1).forEach((element) => element.remove());
      if(!itemsTab.childElementCount) {
        parent.append(this.createNothingFoundElement());
      }

      if(this.mediaTab === mediaTab) {
        this.scrollToStart();
      }

      this.lazyLoadQueue.refresh();
      this.mediaFilterStagingItemsTab = undefined;
      resolve();
    }));
  }

  private restoreCommittedMediaFilterState(mediaTab: SearchSuperMediaTab, itemsTab: HTMLDivElement) {
    const state = this.mediaFilterCommittedState;
    if(!state) {
      return;
    }

    const pendingInputFilter = mediaTab.inputFilter;
    if(pendingInputFilter !== state.inputFilter && this.tabs[pendingInputFilter] !== itemsTab) {
      delete this.tabs[pendingInputFilter];
    }

    mediaTab.inputFilter = state.inputFilter;
    mediaTab.scroll = state.scroll;
    this.refiller.reset('media'); // same swap the other way round — the counters it watches are a different filter's now
    this.tabs[state.inputFilter] = itemsTab;
    this.searchContext.inputFilter = {_: state.inputFilter};
    this.loaded.media = state.loaded;
    this.nextRates.media = state.nextRate;
    this.counters.media = state.counter;
    this.mediaFilterCommittedInputFilter = state.inputFilter;
    this.mediaFilterCommittedState = undefined;
  }

  private discardMediaFilterStagingItemsTab(itemsTab: HTMLDivElement) {
    this.removeFromLazyLoadQueue(itemsTab);
    if(this.mediaFilterStagingItemsTab === itemsTab) {
      this.mediaFilterStagingItemsTab = undefined;
    }
  }

  private removeFromLazyLoadQueue(container: HTMLElement) {
    Array.from(container.children).forEach((element) => {
      this.lazyLoadQueue.delete({div: element as HTMLElement});
    });
  }

  private createNothingFoundElement() {
    const div = document.createElement('div');
    div.append(i18n('Chat.Search.NothingFound'));
    div.classList.add('position-center', 'text-center', 'content-empty', 'no-select');
    return div;
  }

  public filterMessagesByType(messages: MyMessage[], type: SearchSuperType): MyMessage[] {
    return filterMessagesByInputFilter({inputFilter: type, messages: messages, limit: messages.length});
  }

  private async processEmptyFilter({message, searchGroup, mediaTab}: ProcessSearchSuperResult) {
    const isSaved = mediaTab.type === 'saved';

    let peerId = message.peerId;
    if(isSaved) {
      peerId = message.fromId;
    }
    peerId = await this.managers.appPeersManager.getPeerMigratedTo(peerId) || peerId;

    const middleware = this.middleware.get();

    const loadPromises: Promise<any>[] = [];
    const dialogElement = appDialogsManager.addDialogNew({
      dontSetActive: true,
      peerId,
      container: searchGroup?.list || false,
      avatarSize: 'bigger',
      loadPromises,
      wrapOptions: {
        middleware
      },
      withStories: true,
      meAsSaved: !isSaved,
      autonomous: isSaved,
      fromName: !peerId ? getFwdFromName(message.fwd_from) : undefined
    });
    const setLastMessagePromise = appDialogsManager.setLastMessageN({
      dialog: {
        _: 'dialog',
        peerId
      } as any,
      lastMessage: message,
      dialogElement,
      highlightWord: this.searchContext.query,
      noForwardIcon: isSaved
    });

    loadPromises.push(setLastMessagePromise);
    return Promise.all(loadPromises).then(() => {
      if(searchGroup) {
        return;
      }

      return {element: dialogElement.container, message};
    });
  }

  private async processPhotoVideoFilter({message, promises, middleware}: ProcessSearchSuperResult) {
    const media = getMediaFromMessage(message, true);

    const div = document.createElement('div');
    div.classList.add('grid-item');
    // this.log(message, photo);

    let wrapped: Awaited<ReturnType<typeof wrapPhoto>>;
    const size = choosePhotoSize(media, 200, 200);
    if(media._ !== 'photo') {
      wrapped = (await wrapVideo({
        doc: media,
        message,
        container: div,
        boxWidth: 0,
        boxHeight: 0,
        lazyLoadQueue: this.lazyLoadQueue,
        middleware,
        onlyPreview: true,
        withoutPreloader: true,
        noPlayButton: true,
        photoSize: size
      })).thumb;
    } else {
      wrapped = await wrapPhoto({
        photo: media,
        message,
        container: div,
        boxWidth: 0,
        boxHeight: 0,
        lazyLoadQueue: this.lazyLoadQueue,
        middleware,
        withoutPreloader: true,
        noBlur: true,
        size
      });
    }

    const sensitive = this.isMessageSensitive(message);

    if((message.media as MessageMedia.messageMediaPhoto).pFlags.spoiler || sensitive) {
      const mediaSpoiler = await wrapMediaSpoiler({
        animationGroup: 'chat',
        media,
        middleware,
        sensitive,
        width: 140,
        height: 140,
        multiply: 0.3
      });

      div.append(mediaSpoiler);
    }

    [
      wrapped.images.thumb,
      wrapped.images.full
    ].filter(Boolean).forEach((image) => {
      image.classList.add('grid-item-media');
    });

    promises.push(wrapped.loadPromises.thumb);

    return {element: div, message};
  }

  private async processDocumentFilter({message, inputFilter, middleware}: ProcessSearchSuperResult) {
    const document = getMediaFromMessage(message, true) as Document.document;
    const showSender = this.showSender || (['voice', 'round'] as MyDocument['type'][]).includes(document.type);

    const div = await wrapDocument({
      message,
      middleware,
      withTime: !showSender,
      fontWeight: 400,
      voiceAsMusic: true,
      clickable: true,
      showSender,
      searchContext: this.copySearchContext(inputFilter, this.nextRates.files, false),
      lazyLoadQueue: this.lazyLoadQueue,
      autoDownloadSize: 0,
      getSize: () => 320
    });

    if((['audio', 'voice', 'round'] as MyDocument['type'][]).includes(document.type)) {
      div.classList.add('audio-48');
    }

    return {message, element: div};
  }

  private async processUrlFilter({message, promises, middleware}: ProcessSearchSuperResult) {
    let webPage = (message.media as MessageMedia.messageMediaWebPage)?.webpage as WebPage.webPage | WebPage.webPageEmpty;

    if(!webPage) {
      const entity = message.totalEntities ? message.totalEntities.find((e: any) => e._ === 'messageEntityUrl' || e._ === 'messageEntityTextUrl') : null;
      let url: string, display_url: string, sliced: string;

      if(!entity) {
        const match = matchUrl(message.message);
        if(!match) {
          return;
        }

        url = match[0];
      } else {
        sliced = message.message.slice(entity.offset, entity.offset + entity.length);
      }

      if(entity?._ === 'messageEntityTextUrl') {
        url = entity.url;
        // display_url = sliced;
      } else {
        url = url || sliced;
      }

      display_url = url;

      const same = message.message === url;
      if(!url.match(/^(ftp|http|https):\/\//)) {
        display_url = 'https://' + url;
        url = url.includes('@') ? url : 'https://' + url;
      }

      display_url = new URL(display_url).hostname;

      webPage = {
        _: 'webPage',
        pFlags: {},
        url,
        display_url,
        id: '',
        hash: 0
      };

      if(!same) {
        webPage.description = message.message;
      }
    }

    if(webPage._ === 'webPageEmpty') {
      return;
    }

    const previewDiv = document.createElement('div');
    previewDiv.classList.add('preview');

    // this.log('wrapping webpage', webpage);

    if(webPage.photo) {
      const res = wrapPhoto({
        container: previewDiv,
        message: null,
        photo: webPage.photo as Photo.photo,
        boxWidth: 0,
        boxHeight: 0,
        withoutPreloader: true,
        lazyLoadQueue: this.lazyLoadQueue,
        middleware,
        size: choosePhotoSize(webPage.photo as Photo.photo, 60, 60, false),
        loadPromises: promises,
        noBlur: true
      });
    } else {
      previewDiv.classList.add('empty');
      setInnerHTML(previewDiv, wrapAbbreviation(webPage.title || webPage.display_url || webPage.description || webPage.url, true));
    }

    const title = wrapWebPageTitle(webPage);

    const subtitleFragment = wrapWebPageDescription(webPage);
    const aFragment = htmlToDocumentFragment(wrapRichText(webPage.url || ''));
    const a = aFragment.firstElementChild as HTMLAnchorElement;
    const aIsAnchor = a instanceof HTMLAnchorElement;
    if(aIsAnchor) {
      try { // can have 'URIError: URI malformed'
        a.innerText = decodeURIComponent(a.href);
      } catch(err) {

      }
    }

    if(subtitleFragment.firstChild) {
      subtitleFragment.append('\n');
    }

    subtitleFragment.append(a);

    if(this.showSender) {
      subtitleFragment.append('\n', await wrapSenderToPeer(message));
    }

    if(!title.textContent) {
      // title = new URL(webpage.url).hostname;
      title.append(wrapPlainText(webPage.display_url.split('/', 1)[0]));
    }

    const row = renderSearchWebPageRow({
      title,
      titleRight: wrapSentTime(message),
      subtitle: subtitleFragment,
      media: previewDiv,
      link: aIsAnchor ? {
        href: a.href,
        onClick: a.getAttribute('onclick'),
        targetBlank: a.target === '_blank'
      } : undefined,
      middleware
    });

    if(row.innerText.trim().length) {
      return {message, element: row};
    }
  }

  public async performSearchResult({messages, mediaTab, canAnimateIn = false, append = true}: PerformSearchResultArgs) {
    const elemsToAppend: {element: HTMLElement, message: any}[] = [];
    const sharedMediaDiv: HTMLElement = mediaTab.contentTab;
    const promises: Promise<any>[] = [];
    const middleware = this.middleware.get();
    const isSaved = mediaTab.type === 'saved';
    let inputFilter = mediaTab.inputFilter;

    await getHeavyAnimationPromise();

    let searchGroup: SearchGroup;
    if(inputFilter === 'inputMessagesFilterPhotoVideo' && !!this.searchContext.query.trim()) {
      inputFilter = 'inputMessagesFilterEmpty';
      searchGroup = this.searchGroupMedia;
      sharedMediaDiv.append(searchGroup.container);
    } else if(inputFilter === 'inputMessagesFilterEmpty' && !isSaved) {
      searchGroup = this.searchGroups.messages;
    }

    if(canAnimateIn && liteMode.isAvailable('animations') && searchGroup?.container) {
      searchGroup.container.classList.add('is-hidden');

      setTimeout(() => SetTransition({
        element: searchGroup.container,
        className: 'is-visible',
        forwards: true,
        duration: 250,
        onTransitionEnd: () => {
          searchGroup.container.classList.remove('is-hidden');
          searchGroup.container.classList.remove('is-visible');
        }
      }), 100); // doesn't properly animate without it, even useRafs don't really help
    }

    const options: ProcessSearchSuperResult = {
      elemsToAppend,
      inputFilter,
      message: undefined,
      middleware,
      promises,
      searchGroup,
      mediaTab
    };

    let processCallback: (options: ProcessSearchSuperResult) => any;

    // https://core.telegram.org/type/MessagesFilter
    switch(inputFilter) {
      case 'inputMessagesFilterEmpty': {
        processCallback = this.processEmptyFilter;
        break;
      }

      case 'inputMessagesFilterPhotos':
      case 'inputMessagesFilterPhotoVideo':
      case 'inputMessagesFilterVideo': {
        processCallback = this.processPhotoVideoFilter;
        break;
      }

      case 'inputMessagesFilterVoice':
      case 'inputMessagesFilterRoundVoice':
      case 'inputMessagesFilterMusic':
      case 'inputMessagesFilterDocument': {
        processCallback = this.processDocumentFilter;
        break;
      }

      case 'inputMessagesFilterUrl': {
        processCallback = this.processUrlFilter;
        break;
      }

      default:
        // this.log.warn('death is my friend', messages);
        break;
    }

    if(processCallback) {
      processCallback = processCallback.bind(this);

      type K = {element: HTMLElement, message: Message.message | Message.messageService};
      const results: (Promise<K> | K)[] = messages.map(async(message) => {
        try {
          options.message = message as Message.message;
          return await processCallback(options);
        } catch(err) {
          this.log.error('error rendering filter', inputFilter, options, message, err);
        }
      });

      const awaited = (await Promise.all(results)).filter(Boolean);
      elemsToAppend.push(...awaited);
    }

    const showSearchGroupAnyway = mediaTab.type === 'chats' && searchGroup.createPlaceholder;
    if(searchGroup && (searchGroup.list.childElementCount || showSearchGroupAnyway)) {
      searchGroup.setActive();

      if(!searchGroup.list?.childElementCount && searchGroup.createPlaceholder) searchGroup.addPlaceholder(searchGroup.createPlaceholder());
    }

    if(this.loadMutex) {
      promises.push(this.loadMutex);
    }

    if(promises.length) {
      await Promise.all(promises);
      if(!middleware()) {
        // this.log.warn('peer changed');
        return 0;
      }
    }

    const length = elemsToAppend.length;

    if(length) {
      const method = append ? 'append' : 'prepend';
      const threadId = isSaved ? this.searchContext.peerId : undefined;
      const container = this.tabs[inputFilter];
      elemsToAppend.forEach((details) => {
        const {element, message} = details;
        if(!message) {
          debugger;
        }

        element.classList.add('search-super-item');
        element.dataset.mid = '' + message.mid;
        element.dataset.peerId = '' + message.peerId;
        element.dataset.timestamp = '' + message.date;
        threadId && (element.dataset.threadId = '' + threadId);
        container[method](element);

        if(this.selection?.isSelecting) {
          this.selection.toggleElementCheckbox(element, true);
        }
      });

      if(isSaved) {
        let chatlist = sharedMediaDiv.querySelector<HTMLElement>('.chatlist');
        if(!chatlist) {
          chatlist = appDialogsManager.createChatList({new: true});
          container.append(chatlist);

          appDialogsManager.setListClickListener({
            list: chatlist,
            onFound: (el) => {
              if(this.selection.isSelecting) {
                return false;
              }
            },
            withContext: undefined,
            autonomous: true,
            openInner: true
          });
        }
      }
    }

    // if(type !== 'inputMessagesFilterEmpty') {
    this.afterPerforming(inputFilter === 'inputMessagesFilterEmpty' ? 1 : length, mediaTab);
    // }

    return length;
  }

  private afterPerforming(length: number, mediaTab: SearchSuperMediaTab) {
    const contentTab = mediaTab.contentTab;
    if(!contentTab) {
      return;
    }

    if(mediaTab.type === 'media' && this.mediaFilterStagingItemsTab) {
      return;
    }

    if(mediaTab.hideOn) {
      mediaTab.hideOn.classList.remove('hide');
    }

    const parent = contentTab.parentElement;
    Array.from(parent.children).slice(1).forEach((child) => {
      child.remove();
    });

    // this.contentContainer.classList.add('loaded');

    if(!length && !mediaTab.itemsTab.childElementCount) {
      parent.append(this.createNothingFoundElement());
    }
  }

  private loadChats() {
    const renderedPeerIds: Set<PeerId> = new Set();
    const middleware = this.middleware.get();

    for(const i in this.searchGroups) {
      const group = this.searchGroups[i as SearchGroupType];
      group.clear();
      this.tabs.inputMessagesFilterEmpty.append(group.container);
    }

    const query = this.searchContext.query;
    if(query && !this.searchContext.peerId && !this.searchContext.communityId) {
      const addDialogSubtitle = async(dom: DialogDom, peerId: PeerId) => {
        const peer = await this.managers.appPeersManager.getPeer(peerId);
        if(peerId === rootScope.myId) {
          dom.lastMessageSpan.append(i18n('Presence.YourChat'));
        } else {
          let username = await this.managers.appPeersManager.getPeerUsername(peerId);
          if(!username) {
            const user = await this.managers.appUsersManager.getUser(peerId);
            if(user?.phone) {
              username = '+' + formatPhoneNumber(user.phone).formatted;
            }
          } else {
            username = '@' + username;
          }

          // if(query) {
          //   const regExp = new RegExp(`(${escapeRegExp(query)}|${escapeRegExp(cleanSearchText(query))})`, 'gi');
          //   dom.titleSpan.innerHTML = dom.titleSpan.innerHTML.replace(regExp, '<i>$1</i>');
          // }

          const toJoin: (Node | string)[] = [
            username
          ];

          if(/* showMembersCount &&  */((peer as Chat.channel).participants_count || (peer as any).participants)) {
            toJoin.push(await getChatMembersString(peerId.toChatId()));
          }

          dom.lastMessageSpan.append(...join(toJoin.filter(Boolean), false));
        }
      }

      const setResults = (results: PeerId[], group: SearchGroup, showMembersCount = false) => {
        results.map((peerId) => {
          if(renderedPeerIds.has(peerId)) {
            return;
          }

          renderedPeerIds.add(peerId);

          const {dom} = appDialogsManager.addDialogNew({
            peerId: peerId,
            container: group.list,
            avatarSize: 'abitbigger',
            autonomous: group.autonomous,
            wrapOptions: {
              middleware
            },
            withStories: true
          });
          return {dom, peerId};
        }).filter(Boolean).forEach(async({dom, peerId}) => addDialogSubtitle(dom, peerId));

        group.toggle();
      };

      const onLoad = <T>(arg: T) => {
        if(!middleware()) {
          return;
        }

        // this.loadedContacts = true;

        return arg;
      };

      return Promise.all([
        this.managers.appUsersManager.getContactsPeerIds(query, true, undefined, 10)
        .then(onLoad)
        .then((contacts) => {
          if(contacts) {
            setResults(contacts, this.searchGroups.contacts, true);
          }
        }),

        this.managers.appChatsManager.getSponsoredPeers(query)
        .then(onLoad)
        .then((peers) => {
          if(!peers?.length) return;

          for(const peer of peers.reverse()) {
            const {dom} = appDialogsManager.addDialogNew({
              peerId: peer.peer,
              container: this.searchGroups.globalContacts.list,
              append: false,
              avatarSize: 'abitbigger',
              autonomous: this.searchGroups.globalContacts.autonomous,
              wrapOptions: {
                middleware
              }
            });

            dom.containerEl.dataset.sponsored = 'true';

            const chip = ButtonMenuToggle({
              listenerSetter: this.listenerSetter,
              appendTo: getOverlayRoot(),
              buttons: getSponsoredMessageButtons({
                message: peer,
                handleReportAd: () => {
                  showAdReport(peer);
                }
              }),
              onOpen: (e, element) => {
                // position menu
                const rect = chip.getBoundingClientRect();
                const bodyRect = getOverlayRoot().getBoundingClientRect();
                element.style.right = `${bodyRect.width - (rect.left + rect.width)}px`;
                element.style.top = `${rect.top + rect.height + 8}px`;
              },
              direction: 'bottom-left'
            });
            chip.classList.add('sponsored-peer-chip');

            chip.replaceChildren(
              i18n('SponsoredMessageAd'),
              Icon('more')
            )

            dom.lastTimeSpan.appendChild(chip)

            addDialogSubtitle(dom, peer.peer);
          }
        }),

        this.managers.appUsersManager.searchContacts(query, 20)
        .then(onLoad)
        .then((contacts) => {
          if(contacts) {
            setResults(contacts.my_results, this.searchGroups.contacts, true);
            setResults(contacts.results/* .concat(contacts.results, contacts.results, contacts.results) */, this.searchGroups.globalContacts);

            this.searchGroups.globalContacts.container.classList.add('is-short');

            if(this.searchGroups.globalContacts.nameEl.lastElementChild !== this.searchGroups.globalContacts.nameEl.firstElementChild) {
              this.searchGroups.globalContacts.nameEl.lastElementChild.remove();
            }

            if(this.searchGroups.globalContacts.list.childElementCount > 3) {
              this.searchGroups.globalContacts.needShowMoreButton('is-short');
            }
          }
        }),

        this.managers.dialogsStorage.getDialogs({query, offsetIndex: 0, limit: 20, filterId: 0})
        .then(onLoad)
        .then((value) => {
          if(value) {
            setResults(value.dialogs.map((d) => d.peerId), this.searchGroups.contacts, true);
          }
        })
      ]);
    } else if(!this.searchContext.peerId && !this.searchContext.communityId && !this.searchContext.minDate) {
      const [appState] = useAppState();
      const renderRecentSearch = (setActive = true) => {
        if(!middleware()) {
          return;
        }

        this.searchGroups.recent.list.replaceChildren();

        createRoot((dispose) => {
          middleware.onClean(dispose);

          // Keep this panel's order until it reopens, but apply removals immediately.
          const recentSearch = unwrap(appState.recentSearch).slice();
          const arr = For({
            get each() {
              return recentSearch.filter((peerId) => appState.recentSearch.includes(peerId));
            },
            children: (peerId) => {
              const middlewareHelper = createMiddleware();
              const {dom} = appDialogsManager.addDialogNew({
                peerId: peerId,
                container: false,
                meAsSaved: true,
                avatarSize: 'abitbigger',
                autonomous: true,
                wrapOptions: {
                  middleware: middlewareHelper.get()
                },
                withStories: true
              });
              (async() => {
                dom.lastMessageSpan.append(await (peerId.isUser() ?
                  Promise.resolve(getUserStatusString(await this.managers.appUsersManager.getUser(peerId.toUserId()))) :
                  getChatMembersString(peerId.toChatId())));
              })();

              return dom.containerEl;
            }
          });

          const elements = children(() => arr);
          createEffect(() => {
            this.searchGroups.recent.list.replaceChildren(...(elements.toArray() as HTMLElement[]));
          });

          createEffect(() => {
            if(!appState.recentSearch.length) {
              this.searchGroups.recent.clear();
            } else if(setActive) {
              this.searchGroups.recent.setActive();
            }
          });
        });
      };

      return Promise.all([
        createTopPeersList({
          middleware,
          onFound: close,
          group: this.searchGroups.people,
          modifyPeers: (peers) => {
            peers = peers.slice(0, 15);
            const idx = peers.findIndex((peer) => peer.id === rootScope.myId);
            if(idx !== -1) {
              peers = peers.slice();
              peers.splice(idx, 1);
            }

            return peers;
          }
        }).promise,

        renderRecentSearch()
      ]);
    } else return Promise.resolve();
  }

  private async loadMembers({mediaTab}: SearchSuperLoadTypeOptions) {
    const chatId = mediaTab.type === 'members' ? this.searchContext.peerId.toChatId() : undefined;
    const userId = mediaTab.type === 'groups' ? this.searchContext.peerId.toUserId() : undefined;
    const middleware = this.middleware.get();
    let promise: Promise<void>;

    const renderParticipants = async(participants: (ChatParticipant | ChannelParticipant | Chat)[]) => {
      if(this.loadMutex) {
        await this.loadMutex;

        if(!middleware()) {
          return;
        }
      }

      let membersList = this.membersList,
        membersParticipantMap = this.membersParticipantMap,
        membersMiddlewareHelper = this.membersMiddlewareHelper;
      if(!membersList) {
        membersParticipantMap = this.membersParticipantMap = new Map();
        membersMiddlewareHelper = this.membersMiddlewareHelper = getMiddleware();
        membersList = this.membersList = new SortedUserList({
          lazyLoadQueue: this.lazyLoadQueue,
          rippleEnabled: false,
          managers: this.managers,
          middleware
        });
        attachClickEvent(membersList.list, (e) => {
          if(findUpClassName(e.target, 'has-stories')) {
            return;
          }

          const li = findUpTag(e.target, DIALOG_LIST_ELEMENT_TAG);
          if(!li) {
            return;
          }

          const peerId = li.dataset.peerId.toPeerId();
          let promise: Promise<any> = Promise.resolve();
          if(this.slider === appSidebarRight && mediaSizes.isMobile) {
            promise = (this.slider as typeof appSidebarRight).toggleSidebar(false);
          }

          promise.then(() => {
            appImManager.setInnerPeer({peerId});
          });
        });
        mediaTab.itemsTab.append(membersList.list);
        this.afterPerforming(1, mediaTab);

        if(chatId) {
          const middleware = membersMiddlewareHelper.get();
          createParticipantContextMenu({
            chatId,
            listenTo: membersList.list,
            participants: this.membersParticipantMap,
            slider: this.slider,
            middleware
          });

          const deleteByPeerId = (peerId: PeerId) => {
            membersList.ranks.delete(peerId);
            membersList.delete(peerId);
            membersParticipantMap.delete(peerId);
            this.setCounter(mediaTab.type, this.counters[mediaTab.type] - 1);
          };

          const renderParticipant = (participant: ChannelParticipant | ChatParticipant) => {
            renderParticipants([participant]);
            this.setCounter(mediaTab.type, this.counters[mediaTab.type] + 1);
          };

          if(apiManagerProxy.getChat(chatId)._ === 'chat') {
            const onChatFullUpdate = async(_chatId: ChatId) => {
              if(chatId !== _chatId) {
                return;
              }

              const chatFull = await this.managers.appProfileManager.getChatFull(chatId) as ChatFull.chatFull;
              if(!middleware()) {
                return;
              }

              const participants = chatFull.participants as ChatParticipants.chatParticipants;
              const processedPeerIds = new Set<PeerId>();
              for(const participant of participants.participants) {
                const peerId = participant.user_id.toPeerId(false);
                processedPeerIds.add(peerId);
                renderParticipant(participant);
              }

              membersParticipantMap.forEach((participant, peerId) => {
                if(!processedPeerIds.has(peerId)) {
                  deleteByPeerId(peerId);
                }
              });
            };
            rootScope.addEventListener('chat_full_update', onChatFullUpdate);
            middleware.onClean(() => {
              rootScope.removeEventListener('chat_full_update', onChatFullUpdate);
            });
          } else {
            const onParticipantUpdate = (update: Update.updateChannelParticipant) => {
              if(chatId !== update.channel_id) {
                return;
              }

              const peerId = getParticipantPeerId(update.prev_participant || update.new_participant);
              const wasRendered = membersList.has(peerId);
              if(wasRendered || (update.new_participant as ChannelParticipant.channelParticipantBanned).pFlags?.left) {
                deleteByPeerId(peerId);
              }

              if((!update.prev_participant || wasRendered) && update.new_participant) {
                renderParticipant(update.new_participant);
              }
            };
            rootScope.addEventListener('chat_participant', onParticipantUpdate);
            middleware.onClean(() => {
              rootScope.removeEventListener('chat_participant', onParticipantUpdate);
            });
          }
        }
      }

      const peerIds: {
        peerId: PeerId,
        rank: ReturnType<typeof getParticipantRank>,
        participant: typeof participants[0]
      }[] = participants.map((participant) => {
        const peerId = userId ? (participant as Chat.chat).id.toPeerId(true) : getParticipantPeerId(participant as ChannelParticipant);
        if(chatId ? peerId.isAnyChat() : peerId.isUser()) {
          return;
        }

        return {
          peerId,
          rank: getParticipantRank(participant as ChannelParticipant) as any,
          participant
        };
      }).filter(Boolean);

      const filtered = await filterAsync(peerIds, async({peerId}) => {
        const peer: User | Chat = await this.managers.appPeersManager.getPeer(peerId);
        if(!middleware()) {
          return false;
        }

        if(!peer || (peer as User.user).pFlags.deleted) {
          return false;
        }

        return true;
      });

      for(const {peerId, rank, participant} of filtered) {
        if(rank) {
          membersList.ranks.set(peerId, rank);
        } else {
          membersList.ranks.delete(peerId);
        }

        membersParticipantMap.set(peerId, participant as ChannelParticipant);
        if(membersList.has(peerId)) {
          membersList.update(peerId);
        } else {
          membersList.add(peerId);
        }
      }
    };

    if(userId) {
      const LOAD_COUNT = !this.membersList ? 50 : 200;
      promise = this.managers.appUsersManager.getCommonChats(userId, LOAD_COUNT, this.nextRates[mediaTab.type]).then((messagesChats) => {
        if(!middleware()) {
          return;
        }

        const count = (messagesChats as MessagesChats.messagesChatsSlice).count ?? messagesChats.chats.length;
        if(!this.counters[mediaTab.type]) {
          this.setCounter(mediaTab.type, count);
        }

        // const list = mediaTab.contentTab.firstElementChild as HTMLUListElement;
        const lastChat = messagesChats.chats[messagesChats.chats.length - 1];
        this.nextRates[mediaTab.type] = lastChat?.id as number;

        if(messagesChats.chats.length < LOAD_COUNT) {
          this.loaded[mediaTab.type] = true;
        }

        return renderParticipants(messagesChats.chats);
      });
    } else if(await this.managers.appChatsManager.isChannel(chatId)) {
      const LOAD_COUNT = !this.membersList ? 50 : 200;
      promise = this.managers.appProfileManager.getChannelParticipants({
        id: chatId,
        limit: LOAD_COUNT,
        offset: this.nextRates[mediaTab.type]
      }).then((participants) => {
        if(!middleware()) {
          return;
        }

        const list = mediaTab.itemsTab.firstElementChild as HTMLUListElement;
        this.nextRates[mediaTab.type] = (list ? list.childElementCount : 0) + participants.participants.length;

        if(participants.participants.length < LOAD_COUNT) {
          this.loaded[mediaTab.type] = true;
        }

        this.setCounter(mediaTab.type, participants.count);

        return renderParticipants(participants.participants);
      });
    } else {
      promise = this.managers.appProfileManager.getChatFull(chatId).then((chatFull) => {
        if(!middleware()) {
          return;
        }

        // console.log('anymore', chatFull);
        this.loaded[mediaTab.type] = true;
        const participants = (chatFull as ChatFull.chatFull).participants;
        if(participants._ === 'chatParticipantsForbidden') {
          return;
        }

        this.setCounter(mediaTab.type, participants.participants.length);

        return renderParticipants(participants.participants);
      });
    }

    return promise;
  }

  private loadStories({mediaTab}: SearchSuperLoadTypeOptions) {
    if(this.storiesActions) {
      return this.storiesActions.load();
    }

    const middleware = this.middleware.get();
    const promise = deferredPromise<void>();
    createRoot((dispose) => {
      middleware.onClean(() => {
        this.storiesActions = undefined;
        this.storiesSetAlbum = undefined;
        dispose();
        promise.reject();
      });

      const {render: storiesList, actions, setAlbum} = StoriesProfileList({
        peerId: this.searchContext.peerId,
        pinned: !this.storiesArchive,
        archive: this.storiesArchive,
        scrollable: this.scrollable,
        listenerSetter: this.listenerSetter,
        withSelection: true,
        selectionMount: this.navScrollableContainer,
        onReady: () => {
          promise.resolve();
          mediaTab.contentTab.append(getFirstChild(storiesList, v => v instanceof Element) as Element);
          this.afterPerforming(1, mediaTab);
        },
        onLoad: (loaded: boolean) => {
          this.loaded[mediaTab.type] = loaded;
        },
        onCountChange: (length: number) => {
          this.setCounter(mediaTab.type, length);
        }
      });
      this.storiesActions = actions;
      this.storiesSetAlbum = setAlbum;
      this.storiesActions.load();
    });
    return promise;
  }

  private async loadSimilarChannels({mediaTab}: SearchSuperLoadTypeOptions) {
    const middlewareHelper = this.middleware.get().create();

    const renderChats = async(chats: Chat[], middleware: Middleware) => {
      const chatlist = appDialogsManager.createChatList({new: true});

      const promises = chats.map(async(chat) => {
        const loadPromises: Promise<any>[] = [];
        const {dom} = appDialogsManager.addDialogNew({
          peerId: chat.id.toPeerId(true),
          container: chatlist,
          avatarSize: 'abitbigger',
          autonomous: false,
          wrapOptions: {
            middleware
          },
          loadPromises
        });
        dom.lastMessageSpan.append(await getChatMembersString(chat.id, this.managers, chat));

        return Promise.all(loadPromises);
      });

      appDialogsManager.setListClickListener({list: chatlist, autonomous: true});

      await Promise.all(promises);
      return chatlist;
    };

    const createPaywall = (limit: number) => {
      const wall = document.createElement('div');
      wall.classList.add('similar-channels-paywall');
      const btn = Button('btn-primary btn-color-primary', {icon: 'premium_unlock_filled', text: 'UnlockSimilar'});
      btn.classList.add('similar-channels-paywall-button');
      const onClick = () => showPremiumPopup();
      const anchor = anchorCallback(onClick);
      attachClickEvent(btn, onClick);
      anchor.classList.add('primary');
      const subtitle = i18n('SimilarChannels.Unlock', [anchor, limit]);
      subtitle.classList.add('similar-channels-paywall-subtitle');
      wall.append(btn, subtitle);
      return wall;
    };

    let paywall: HTMLElement, wasPremium: boolean;
    const onPremium = async(isPremium: boolean) => {
      if(wasPremium === isPremium) {
        return;
      }

      middlewareHelper.clean();
      const middleware = middlewareHelper.get();

      const [messagesChats, premiumLimit, isPremiumFeaturesHidden] = await Promise.all([
        this.managers.appChatsManager.getChannelRecommendations(this.searchContext.peerId.toChatId()),
        this.managers.apiManager.getLimit('recommendedChannels', true),
        apiManagerProxy.isPremiumFeaturesHidden()
      ]);

      const chatlist = await renderChats(messagesChats.chats, middleware);
      if(!middleware()) {
        return;
      }

      mediaTab.itemsTab.replaceChildren(chatlist);
      this.afterPerforming(1, mediaTab);
      this.loaded[mediaTab.type] = true;

      const count = (messagesChats as MessagesChats.messagesChatsSlice).count ?? messagesChats.chats.length;
      this.setCounter(mediaTab.type, count);

      if(!isPremium && !isPremiumFeaturesHidden) {
        paywall ||= createPaywall(premiumLimit);
        mediaTab.itemsTab.append(paywall);
      }
    };

    rootScope.addEventListener('premium_toggle', onPremium);
    this.middleware.get().onClean(() => {
      rootScope.removeEventListener('premium_toggle', onPremium);
    });

    return onPremium(rootScope.premium);
  }

  private async loadSavedDialogs({mediaTab, middleware, side}: SearchSuperLoadTypeOptions) {
    if(this._loadSavedDialogs) {
      return this._loadSavedDialogs(side);
    }

    const xd = new AutonomousSavedDialogList({appDialogsManager});
    xd.scrollable = this.scrollable;
    xd.sortedList = new SortedDialogList({
      appDialogsManager,
      managers: this.managers,
      log: this.log,
      requestItemForIdx: xd.requestItemForIdx,
      onListShrinked: xd.onListShrinked,
      itemSize: 72,
      scrollable: this.scrollable,
      indexKey: 'index_0',
      virtualFilterId: rootScope.myId,
      extraPaddingBottom: 0
    });

    const list = xd.sortedList.list;

    appDialogsManager.setListClickListener({
      list,
      withContext: true,
      openInner: this.openSavedDialogsInner
    });

    // the pinned sublists of Saved Messages reorder like the pinned chats of a folder
    xd.attachPinnedReorder();

    const getCount = async() => {
      const result = await this.managers.dialogsStorage.getDialogs({filterId: rootScope.myId});
      return result.count;
    };

    const onAnyUpdate = xd.onAnyUpdate = async() => {
      if(!middleware()) return;
      const count = await getCount();
      this.setCounter(mediaTab.type, count);
    };

    onAnyUpdate();

    mediaTab.itemsTab.append(list);
    this.afterPerforming(1, mediaTab);

    this._loadSavedDialogs = () => Promise.resolve(xd.onChatsScroll());
    middleware.onClean(() => {
      xd.destroy();
      this._loadSavedDialogs = undefined;
    });

    return xd.onChatsScroll();
  }

  private async renderPeerDialogs(peerIds: PeerId[], group: SearchGroup, middleware: Middleware, type?: 'bots' | 'channels') {
    if(!middleware()) return;

    for(const peerId of peerIds) {
      const {dom} = appDialogsManager.addDialogNew({
        peerId,
        container: group.list,
        avatarSize: 'abitbigger',
        wrapOptions: {
          middleware
        }
      });
      const peer = await this.managers.appPeersManager.getPeer(peerId);
      const username = await this.managers.appPeersManager.getPeerUsername(peerId);

      if('participants_count' in peer) {
        dom.lastMessageSpan.append(await getChatMembersString(peerId.toChatId()));
      } else if('bot_active_users' in peer) {
        dom.lastMessageSpan.append(i18n('BotUsers', [numberThousandSplitter(peer.bot_active_users)]));
      } else if(username) {
        dom.lastMessageSpan.append('@' + username);
      } else if(type === 'bots') {
        dom.lastMessageSpan.append(i18n('UnknownBotUsers'));
      }
    };
  }

  private async loadChannels({mediaTab, middleware}: SearchSuperLoadTypeOptions) {
    if(this.searchContext.query) {
      const group = createSearchGroup({name: 'Channels', type: 'channels', middleware});
      group.setActive();
      group.nameEl.style.display = 'none';

      const SEARCH_LIMIT = 200; // will get filtered anyway
      const {results: globalResults} = await this.managers.appUsersManager.searchContacts(this.searchContext.query, SEARCH_LIMIT);
      const filteredResultsWithUndefined = await Promise.all(
        globalResults.map(async(user) => await this.managers.appPeersManager.isBroadcast(user) ? user : undefined)
      );
      const filteredResults = filteredResultsWithUndefined.filter(Boolean);

      this.renderPeerDialogs(filteredResults.map((user) => user.toPeerId(true)), group, middleware);

      if(filteredResults.length) {
        mediaTab.itemsTab.append(group.container);
      }
      this.afterPerforming(filteredResults.length, mediaTab);

      this.loaded[mediaTab.type] = true;
      return;
    }

    const dialogs = await this.managers.dialogsStorage.getCachedDialogs();
    const channelDialogsWithUndefined = await Promise.all(dialogs.map(async(dialog) => await this.managers.appPeersManager.isBroadcast(dialog.peerId) ? dialog : undefined));
    const channelDialogs = channelDialogsWithUndefined.filter(Boolean);

    if(channelDialogs.length) {
      const group = createSearchGroup({name: 'Chat.Search.JoinedChannels', type: 'channels', middleware});
      group.setActive();
      mediaTab.itemsTab.append(group.container);

      const SHOW_MORE_LIMIT = 5;
      if(channelDialogs.length > SHOW_MORE_LIMIT) group.needShowMoreButton();

      this.renderPeerDialogs(channelDialogs.map((dialog) => dialog.peerId), group, middleware);
    }

    const recommendations = await this.managers.appChatsManager.getChannelRecommendations();

    if(recommendations.chats.length) {
      const group = createSearchGroup({name: 'SimilarChannels', type: 'channels', middleware});
      group.setActive();
      mediaTab.itemsTab.append(group.container);

      this.renderPeerDialogs(recommendations.chats.map((chat) => chat.id.toPeerId(true)), group, middleware);
    }

    this.afterPerforming(1, mediaTab);
    this.loaded[mediaTab.type] = true;
  }

  private async loadApps({mediaTab, middleware}: SearchSuperLoadTypeOptions) {
    if(this._loadMoreApps) {
      return this._loadMoreApps();
    }

    const onClick = (el: HTMLElement): boolean => {
      (async() => {
        const peerId = el.dataset.peerId.toPeerId();
        const userFull = await this.managers.appProfileManager.getProfile(peerId);
        appImManager.openWebApp({
          botId: peerId.toUserId(),
          url: (userFull.bot_info.menu_button as BotMenuButton.botMenuButton).url,
          buttonText: (userFull.bot_info.menu_button as BotMenuButton.botMenuButton).text,
          fromBotMenu: true,
          peerId
        });
      })();
      return false;
    };

    if(this.searchContext.query) {
      const group = createSearchGroup({name: 'ChatList.Filter.Bots', type: 'apps', onFound: onClick, middleware});
      group.setActive();

      const SEARCH_LIMIT = 200; // will get filtered anyway
      const {results: globalResults} = await this.managers.appUsersManager.searchContacts(this.searchContext.query, SEARCH_LIMIT);
      const filteredResultsWithUndefined = await Promise.all(
        globalResults.map(async(user) => await this.managers.appPeersManager.isBot(user) ? user : undefined)
      );
      const filteredResults = filteredResultsWithUndefined.filter(Boolean);

      this.renderPeerDialogs(filteredResults.map((user) => user.toPeerId(false)), group, middleware, 'bots');

      if(filteredResults.length) {
        mediaTab.itemsTab.append(group.container);
      }
      this.afterPerforming(filteredResults.length, mediaTab);

      this.loaded[mediaTab.type] = true;
      return;
    }

    const myTopApps = await rootScope.managers.appUsersManager.getTopPeers('bots_app');

    if(myTopApps.length) {
      const group = createSearchGroup({name: 'MiniApps.Apps', type: 'apps', onFound: onClick, middleware});
      group.setActive();
      mediaTab.itemsTab.append(group.container);

      const SHOW_MORE_LIMIT = 5;
      if(myTopApps.length > SHOW_MORE_LIMIT) group.needShowMoreButton();

      this.renderPeerDialogs(myTopApps.map((app) => app.id.toPeerId(false)), group, middleware, 'bots');
    }

    const group = createSearchGroup({name: 'MiniApps.Popular', type: 'apps', onFound: onClick, middleware});
    group.setActive();
    mediaTab.itemsTab.append(group.container);

    type GetPopularAppsResult = ReturnType<typeof rootScope.managers.appAttachMenuBotsManager.getPopularAppBots>;
    let currentOffset: string = '', loadPromise: GetPopularAppsResult;
    const APPS_LIMIT_PER_LOAD = 20;

    const loadMoreApps = async() => {
      if(
        loadPromise ||
        !middleware() ||
        currentOffset === undefined
      ) return;

      loadPromise = rootScope.managers.appAttachMenuBotsManager.getPopularAppBots(currentOffset, APPS_LIMIT_PER_LOAD);
      const {nextOffset, userIds} = await loadPromise;

      await this.renderPeerDialogs(userIds.map((id) => id.toPeerId(false)), group, middleware, 'bots');

      currentOffset = nextOffset || undefined;
      loadPromise = undefined;

      if(!currentOffset) {
        this.loaded[mediaTab.type] = true;
      }
    }

    this._loadMoreApps = loadMoreApps;
    await loadMoreApps();

    middleware.onClean(() => {
      this._loadMoreApps = undefined;
    });

    this.afterPerforming(1, mediaTab);
  }

  globalPostsSearch: ReturnType<typeof wrapGlobalPostsSearch>;
  private async loadPosts({mediaTab, middleware}: SearchSuperLoadTypeOptions) {
    if(!this.globalPostsSearch) {
      this.globalPostsSearch = wrapGlobalPostsSearch({
        middleware,
        query: this.searchContext.query
      });
      mediaTab.itemsTab.append(this.globalPostsSearch.dom);
    }

    this.globalPostsSearch.loadMore();
  }

  private loadGifts(): Promise<void> {
    const mediaTab = this.mediaTabsMap.get('gifts');
    if(!mediaTab) return Promise.resolve();

    if(!this.stargiftsStore) {
      const middleware = this.middleware.get();
      createRoot((dispose) => {
        middleware.onClean(() => dispose());

        const scrollTarget = this.scrollable.container;

        const {render: giftsList, store, actions, setCollection} = StarGiftsProfileTab({
          peerId: this.searchContext.peerId,
          scrollParent: scrollTarget,
          onCountChange: (count) => {
            this.setCounter('gifts', count);

            mediaTab.menuTab.classList.toggle('hide', count === 0);
            let needChangeActive = false
            if(count === 0) {
              needChangeActive = mediaTab.menuTab.classList.contains('active');
              mediaTab.menuTab.classList.remove('active');
            }
            this.updateContainerHidden(needChangeActive);
          }
        });
        createEffect(on(() => store.items, (items) => {
          if(items.length > 0 && store.chosenCollection === ALL_COLLECTIONS_ID) {
            this.setPinnedGifts(unwrap(items));
          }
        }))
        this.stargiftsStore = store;
        this.stargiftsActions = actions;
        this.stargiftsSetCollection = setCollection;

        mediaTab.itemsTab.append(getFirstChild(giftsList, (v) => v instanceof Element) as Element);

        if(this.mediaTab?.type === 'gifts') {
          this.onChangeTab?.(this.mediaTab);
        }
      });
      return Promise.resolve();
    }

    if(this.stargiftsStore.loading || this.stargiftsStore.loaded) {
      return Promise.resolve();
    }

    return this.stargiftsActions.loadNext();
  }

  private loadType(options: SearchSuperLoadTypeOptions) {
    const {
      mediaTab,
      justLoad,
      loadCount,
      middleware,
      side
    } = options;
    const {type, inputFilter} = mediaTab;


    let promise = this.loadPromises[type];
    if(promise) {
      return promise;
    }

    if(type === 'members' || type === 'groups') {
      promise = this.loadMembers(options);
    } else if(type === 'stories') {
      promise = this.loadStories(options);
    } else if(type === 'similar') {
      promise = this.loadSimilarChannels(options);
    } else if(type === 'savedDialogs') {
      promise = this.loadSavedDialogs(options);
    } else if(type === 'channels') {
      promise = this.loadChannels(options);
    } else if(type === 'apps') {
      promise = this.loadApps(options);
    } else if(type === 'posts') {
      promise = this.loadPosts(options);
    } else if(type === 'gifts') {
      promise = this.loadGifts();
    }

    if(promise) {
      return this.loadPromises[type] = promise.finally(() => {
        if(!middleware()) {
          return;
        }

        this.loadPromises[type] = null;

        this.refiller.schedule(type, middleware);
      });
    }

    const history = this.historyStorage[inputFilter] ??= [];

    if(inputFilter === 'inputMessagesFilterEmpty' && !history.length && type !== 'saved') {
      if(!this.loadedChats) {
        this.loadChats();
        this.loadedChats = true;
      }

      if(
        !this.searchContext.query.trim() &&
        !this.searchContext.peerId &&
        !this.searchContext.communityId &&
        !this.searchContext.minDate
      ) {
        this.loaded[type] = true;
        return Promise.resolve();
      }
    }

    promise = this.loadPromises[type] = Promise.resolve().then(async() => {
      // render from cache
      if(history.length && this.usedFromHistory[inputFilter] < history.length && !justLoad) {
        const messages: any[] = [];
        let used = Math.max(0, this.usedFromHistory[inputFilter]);
        let slicedLength = 0;

        do {
          const ids = history.slice(used, used + loadCount);
          used += ids.length;
          slicedLength += ids.length;

          // * a mid can have no message behind it (deleted, or a synthetic bound), skip such holes
          const notFilteredMessages = ids.map((m) => apiManagerProxy.getMessageByPeer(m.peerId, m.mid)).filter(Boolean);
          // const notFilteredMessages = await Promise.all(promises);

          messages.push(...this.filterMessagesByType(notFilteredMessages, inputFilter));
        } while(slicedLength < loadCount && used < history.length);

        // если перебор
        /* if(slicedLength > loadCount) {
          let diff = messages.length - loadCount;
          messages = messages.slice(0, messages.length - diff);
          used -= diff;
        } */

        this.usedFromHistory[inputFilter] = used;
        // if(messages.length) {
        return this.performSearchResult({messages, mediaTab}).finally(() => {
          this.refiller.schedule(type, middleware);
        });
        // }
      }

      const lastItem = history[history.length - 1];
      const offsetId = lastItem?.mid || 0;
      const offsetPeerId = lastItem?.peerId || NULL_PEER_ID;

      const options: Parameters<AppMessagesManager['getHistory']>[0] = {
        ...this.searchContext,
        inputFilter: {_: inputFilter},
        offsetId,
        offsetPeerId,
        limit: loadCount,
        nextRate: this.nextRates[type] ??= 0,
        ...(type === 'saved' ? {inputFilter: undefined, peerId: rootScope.myId, threadId: this.searchContext.peerId} : {})
      };
      const value = await this.managers.appMessagesManager.getHistory(options);

      let messages = value.messages;
      if(!messages && value.history/*  && mediaTab.type === 'saved' */) {
        messages = value.history.map((mid) => apiManagerProxy.getMessageByPeer(options.peerId, mid));
      }

      // * a mid can have no message behind it (deleted, or a synthetic bound), skip such holes
      messages = messages.filter(Boolean);

      history.push(...messages.map((m) => ({mid: m.mid, peerId: m.peerId})));

      if(!this.counters[type]) {
        this.setCounter(type, value.count);
      }

      if(!middleware()) {
        // this.log.warn('peer changed');
        return;
      }

      // ! Фикс случая, когда не загружаются документы при открытой панели разработчиков (происходит из-за того, что не совпадают критерии отбора документов в getSearch)
      if(
        value.history.length < loadCount ||
        (this.searchContext.folderId !== undefined && !value.nextRate) ||
        // value.history.length === value.count
        value.isEnd.top
      ) {
      // if((value.count || history.length === value.count) && history.length >= value.count) {
        // this.log(logStr + 'loaded all media', value, loadCount);
        this.loaded[type] = true;
      }

      this.nextRates[type] = value.nextRate;

      if(justLoad) {
        return;
      }

      this.usedFromHistory[inputFilter] = history.length;

      if(!this.loaded[type]) {
        promise.then(() => {
          setTimeout(() => {
            if(!middleware()) return;
            // this.log('will preload more');
            if(this.mediaTab === mediaTab) {
              const promise = this.load(true, true);
              if(promise) {
                promise.then(() => {
                  if(!middleware()) return;
                  // this.log('preloaded more');
                  this.refiller.schedule(type, middleware);
                });
              }
            }
          }, 0);
        });
      }

      // if(value.history.length) {
      return this.performSearchResult({messages: this.filterMessagesByType(messages, inputFilter), mediaTab, canAnimateIn: !offsetId});
      // }
    }).catch((err) => {
      this.log.error('load error:', err);
    }).finally(() => {
      this.loadPromises[type] = null;
    });

    return promise;
  }

  /**
   * How far a tab has got, for `ScrollableRefiller`: fetched messages plus the
   * ones already rendered out of them. Both only ever grow within a peer (and
   * `cleanup` resets the refiller along with them), which is what makes the
   * refill chain terminate.
   *
   * This is the exact state behind `canLoadMediaTab`'s second clause: the
   * `justLoad` preload grows `historyStorage` WITHOUT rendering, and the only
   * thing that renders the remainder into a list too short to scroll is the
   * chain. A tab with no `inputFilter` — saved dialogs, stories, gifts, apps,
   * posts — has no such state and no cache to drain, so it reports a flat 0 and
   * gets the one check after a load that asks "is the viewport full yet"; its
   * list owns whatever paging comes after that.
   */
  private getMediaTabProgress(type: SearchSuperMediaType) {
    const inputFilter = this.mediaTabsMap.get(type)?.inputFilter;
    if(!inputFilter) {
      return 0;
    }

    return Math.max(0, this.usedFromHistory[inputFilter] ?? 0) + (this.historyStorage[inputFilter]?.length ?? 0);
  }

  private canLoadMediaTab(mediaTab: SearchSuperMediaTab) {
    if(mediaTab.type === 'gifts') {
      return !this.stargiftsStore || (!this.stargiftsStore.loading && !this.stargiftsStore.loaded);
    }

    const inputFilter = mediaTab.inputFilter;
    return !this.loaded[mediaTab.type] || (this.historyStorage[inputFilter] && this.usedFromHistory[inputFilter] < this.historyStorage[inputFilter].length);
  }

  private isMessageSensitive(message: Message.message) {
    return isSensitive((usePeer(message.peerId) as User.user).restriction_reason || []) || isMessageSensitive(message);
  }

  public getSearchCounters(filters: MessagesFilter[], canCache = true) {
    const {peerId, threadId} = this.searchContext;
    return this.managers.appMessagesManager.getSearchCounters(peerId, filters, canCache, threadId);
  }

  private async loadFirstTime() {
    const middleware = this.middleware.get();
    const {peerId, threadId} = this.searchContext;
    if(!this.hideEmptyTabs) {
      return;
    }

    const mediaTabs = this.mediaTabs.filter(isCounterDrivenMediaTab);
    const filterTypes = new Set(mediaTabs.map((mediaTab) => mediaTab.inputFilter));
    if(this.mediaTabsMap.has('media')) {
      filterTypes.add('inputMessagesFilterPhotos');
      filterTypes.add('inputMessagesFilterVideo');
    }
    const filters = Array.from(filterTypes, (inputFilter) => ({_: inputFilter} as MessagesFilter));

    const [
      counters,
      canViewSavedDialogs,
      canViewSaved,
      canViewMembers,
      canViewGroups,
      canViewStories,
      canViewSimilar,
      canViewGifts,
      profileTabsData,
      maybePinnedGifts
    ] = await Promise.all([
      this.getSearchCounters(filters),
      this.canViewSavedDialogs(),
      this.canViewSaved(),
      this.canViewMembers(),
      this.canViewGroups(),
      this.canViewStories(),
      this.canViewSimilar(),
      this.canViewGifts(),
      this.getProfileTabsData(),
      peerId === rootScope.myId && this.managers.appGiftsManager.getPinnedGifts(peerId)
    ]);

    if(!middleware()) {
      return;
    }

    if(this.loadMutex) {
      await this.loadMutex;

      if(!middleware()) {
        return;
      }
    }

    if(this.mediaTabsMap.has('media')) {
      const photos = counters.find((counter) => counter.filter._ === 'inputMessagesFilterPhotos')?.count;
      const videos = counters.find((counter) => counter.filter._ === 'inputMessagesFilterVideo')?.count;
      this.setMediaCounters({photos, videos});
    }

    let firstMediaTab: SearchSuperMediaTab;
    let count = 0;
    mediaTabs.forEach((mediaTab) => {
      const counter = counters.find((c) => c.filter._ === mediaTab.inputFilter);

      mediaTab.menuTab.classList.toggle('hide', !counter.count);
      mediaTab.menuTab.classList.remove('active');
      // mediaTab.contentTab.classList.toggle('hide', !counter.count);

      this.setCounter(mediaTab.type, counter.count);

      if(counter.count) {
        firstMediaTab ??= mediaTab;
        ++count;
      }
    });

    const savedDialogsTab = this.mediaTabsMap.get('savedDialogs');
    const savedTab = this.mediaTabsMap.get('saved');
    const membersTab = this.mediaTabsMap.get('members');
    const storiesTab = this.mediaTabsMap.get('stories');
    const groupsTab = this.mediaTabsMap.get('groups');
    const similarTab = this.mediaTabsMap.get('similar');
    const giftsTab = this.mediaTabsMap.get('gifts');

    const giftsCount = profileTabsData.giftsCount ?? 0;
    const showGiftsTab = canViewGifts && giftsCount !== 0;

    const a: [SearchSuperMediaTab, boolean][] = [
      [savedDialogsTab, canViewSavedDialogs],
      [savedTab, canViewSaved],
      [storiesTab, canViewStories],
      [membersTab, canViewMembers],
      [groupsTab, canViewGroups],
      [similarTab, canViewSimilar],
      [giftsTab, showGiftsTab]
    ];

    a.forEach(([tab, value]) => {
      if(!tab) {
        return;
      }

      tab.menuTab.classList.toggle('hide', !value);

      if(value) {
        ++count;
      }
    });

    this.setCounter('gifts', giftsCount);

    if(canViewStories) {
      firstMediaTab = storiesTab;

      const newTitle = i18n(peerId.isUser() ? 'Stories' : 'ProfileStories');
      storiesTab.menuTabName.replaceWith(storiesTab.menuTabName = newTitle);
    }

    if(canViewMembers) {
      firstMediaTab = membersTab;
    }

    if(canViewSavedDialogs) {
      firstMediaTab = savedDialogsTab;
    }

    if(showGiftsTab && !firstMediaTab) {
      firstMediaTab = giftsTab;
    }

    if(maybePinnedGifts) {
      this.setPinnedGifts(maybePinnedGifts);
    }

    const mainMediaTabType = getMediaTypeForProfileTab(profileTabsData.mainTab);
    this.setMainMediaTab(mainMediaTabType);
    const mainMediaTab = this.mediaTabs.find((mediaTab) => mediaTab.type === mainMediaTabType);
    if(mainMediaTab && !mainMediaTab.menuTab.classList.contains('hide')) {
      firstMediaTab = mainMediaTab;
    }

    this.toggleContainerHidden(!firstMediaTab);
    if(firstMediaTab) {
      this.skipScroll = true;
      this.selectTab(this.mediaTabs.indexOf(firstMediaTab), false);
      // firstMediaTab.menuTab.classList.add('active');

      const isSingle = count <= 1;
      this.navScrollableContainer.classList.toggle('is-single', isSingle);
      this.menuGradient.classList.toggle('hide', isSingle);
    }
  }

  private toggleContainerHidden(hidden: boolean) {
    this.container.classList.toggle('hide', hidden);
    this.container.parentElement.classList.toggle('search-empty', hidden);
  }

  private updateContainerHidden(changeActive = false) {
    this.setMainMediaTab(this.mainMediaTabType);
    const visibleTabs = this.mediaTabs.filter((tab) => !tab.menuTab.classList.contains('hide'));
    this.toggleContainerHidden(visibleTabs.length === 0);
    const isSingle = visibleTabs.length <= 1;
    this.navScrollableContainer.classList.toggle('is-single', isSingle);
    this.menuGradient.classList.toggle('hide', isSingle);
    if(changeActive && visibleTabs.length) {
      this.selectTab(this.mediaTabs.indexOf(visibleTabs[0]), false);
    }
  }

  public async load(single = false, justLoad = false, side: 'top' | 'bottom' = 'bottom') {
    const peerId = this.searchContext.peerId;
    this.log('load', single, peerId, this.loadPromises);
    const middleware = this.middleware.get();

    if(this.firstLoad) {
      await (this.loadFirstTimePromise ??= this.loadFirstTime());
      if(!middleware()) {
        return;
      }

      this.loadFirstTimePromise = undefined;
      this.firstLoad = false;
    }

    let toLoad = single ? [this.mediaTab] : this.mediaTabs.filter((t) => t !== this.mediaTab);
    toLoad = toLoad.filter((mediaTab) => {
      return this.canLoadMediaTab(mediaTab);
    });

    if(peerId.isUser()) {
      findAndSplice(toLoad, (mediaTab) => mediaTab.type === 'members');
    } else {
      findAndSpliceAll(toLoad, (mediaTab) => mediaTab.type === 'groups');
    }

    if(!toLoad.length) {
      return;
    }

    const loadCount = justLoad ? 50 : Math.round((windowSize.height / 130 | 0) * 3 * 1.25); // that's good for all types

    const promises: Promise<any>[] = toLoad.map((mediaTab) => {
      return this.loadType({
        mediaTab,
        justLoad,
        loadCount,
        middleware,
        side
      });
    });

    return Promise.all(promises).catch((err) => {
      this.log.error('Load error all promises:', err);
    });
  }


  public setPinnedGifts(gifts: MyStarGift[]) {
    const giftsTab = this.mediaTabsMap.get('gifts')
    const menuTabName = giftsTab?.menuTabName
    if(!menuTabName) return;
    menuTabName.classList.add('search-super-pinned-gifts-wrap');
    Promise.all(gifts.slice(0, 3).map(async(gift) => {
      const div = document.createElement('div');
      await wrapSticker({
        div,
        static: true,
        doc: gift.sticker,
        middleware: this.middleware.get(),
        width: 18,
        height: 18
      }).then(({render}) => render);
      return div
    })).then((gifts) => {
      let wrap = menuTabName.querySelector('.search-super-pinned-gifts')
      if(gifts.length === 0) {
        wrap?.remove();
        return;
      }

      if(!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'search-super-pinned-gifts';
        menuTabName.append(wrap);
      }
      wrap.replaceChildren(...gifts);
    })
  }

  public async canViewSavedDialogs() {
    if(this.searchContext.peerId !== rootScope.myId || this.searchContext.threadId || !this.mediaTabsMap.has('savedDialogs')) {
      return false;
    }

    try {
      await this.managers.dialogsStorage.getDialogs({
        filterId: rootScope.myId
      });

      return true;
    } catch(err) {
      return false;
    }
  }

  public canViewSaved() {
    const {peerId, threadId} = this.searchContext;
    if(threadId || rootScope.myId === peerId) {
      return false;
    }

    return this.managers.appMessagesManager.getHistory({
      peerId: rootScope.myId,
      threadId: this.searchContext.peerId,
      limit: 50
    }).then((historyResult) => {
      return !!historyResult.count;
    }).catch(() => {
      return false;
    });
  }

  public canViewMembers() {
    const {peerId} = this.searchContext;
    const isAnyChat = peerId.isAnyChat();
    if(!isAnyChat || !this.mediaTabsMap.has('members')) return Promise.resolve(false);
    const chatId = peerId.toChatId();
    return Promise.all([
      this.managers.appChatsManager.isBroadcast(chatId),
      this.managers.appChatsManager.hasRights(chatId, 'view_participants'),
      this.managers.appChatsManager.isForum(chatId)
    ]).then(([isBroadcast, hasRights, isForum]) => {
      return !isBroadcast && hasRights && (!this.searchContext.threadId || !isForum);
    });
  }

  public async canViewGroups() {
    const {peerId} = this.searchContext;
    if(!peerId.isUser() || !this.mediaTabsMap.has('groups')) return false;
    const userFull = await this.managers.appProfileManager.getProfile(peerId.toUserId());
    return !!userFull.common_chats_count;
  }

  public async canViewStories() {
    const {peerId, threadId} = this.searchContext;
    if(!this.mediaTabsMap.has('stories') || threadId/*  || !this.onStoriesLengthChange */) {
      return false;
    }

    if(peerId === rootScope.myId) {
      return false;
    }

    if(peerId.isUser()) {
      const promise = this.storiesArchive ?
        this.managers.appStoriesManager.getStoriesArchive(peerId, 1) :
        this.managers.appStoriesManager.getPinnedStories(peerId, 1);
      return promise.then(({count}) => !!count).catch(() => false);
    }

    const chatFull = await this.managers.appProfileManager.getChatFull(peerId.toChatId());
    return !!(chatFull as ChatFull.channelFull).pFlags.stories_pinned_available;
  }

  public async canViewSimilar() {
    const {peerId} = this.searchContext;
    if(peerId.isUser()) {
      return false;
    }

    try {
      const messagesChats = await this.managers.appChatsManager.getChannelRecommendations(peerId.toChatId());
      return !!messagesChats.chats.length;
    } catch(err) {
      return false;
    }
  }

  public canViewGifts() {
    return !this.searchContext.threadId && this.mediaTabsMap.has('gifts');
  }

  public async getProfileTabsData() {
    const {peerId, threadId} = this.searchContext;
    if(threadId) {
      return {};
    }

    const full = await this.managers.appProfileManager.getProfileByPeerId(peerId);
    const profile = full as UserFull.userFull | ChatFull.channelFull;
    return {
      giftsCount: profile.stargifts_count ?? 0,
      mainTab: this.useMainProfileTab ? profile.main_tab : undefined
    };
  }

  public cleanup() {
    ++this.mediaFilterChangeId;
    ++this.mediaCountersRefreshId;
    ++this.mediaCountersVersion;
    this.mediaCounters = {};
    this.onMediaCountersChange?.();
    this.mediaFilterCommittedState = undefined;
    this.mediaFilterStagingItemsTab = undefined;
    const mediaTab = this.mediaTabsMap.get('media');
    if(mediaTab?.inputFilter && mediaTab.itemsTab) {
      this.mediaFilterCommittedInputFilter = mediaTab.inputFilter as SearchSuperMediaInputFilter;
      this.tabs[mediaTab.inputFilter] = mediaTab.itemsTab as HTMLDivElement;
    }
    this.loadPromises = {};
    this.loaded = {};
    this.refiller.reset();
    this.loadedChats = false;
    this.nextRates = {};
    this.firstLoad = true;
    this.prevTabId = -1;
    this.counters = {};
    this.setMainMediaTab();

    this.lazyLoadQueue.clear();

    this.mediaTabs.forEach((mediaTab) => {
      const {inputFilter} = mediaTab;
      if(!inputFilter) {
        return;
      }

      this.usedFromHistory[inputFilter] = -1;
    });

    if(this.selection?.isSelecting) {
      this.selection.cancelSelection();
    }

    // * must go to first tab (это костыль)
    /* const membersTab = this.mediaTabsMap.get('members');
    if(membersTab) {
      const tab = this.canViewMembers() ? membersTab : this.mediaTabs[this.mediaTabs.indexOf(membersTab) + 1];
      this.mediaTab = tab;
    } */

    this.middleware.clean();
    this.loadFirstTimePromise = undefined;
    this.cleanScrollPositions();

    this.membersList = undefined;
    this.membersParticipantMap = undefined;
    this.membersMiddlewareHelper?.destroy();
    this.membersMiddlewareHelper = undefined;
    this.globalPostsSearch = undefined;
  }

  public cleanScrollPositions() {
    this.mediaTabs.forEach((mediaTab) => {
      mediaTab.scroll = undefined;
    });
  }

  public updateScrollDateBadge(canShow: boolean) {
    const mediaTab = this.mediaTab;
    if(!canShow || !supportsSharedMediaScrollDate(mediaTab?.type)) {
      this.scrollDateBadge?.hide();
      return;
    }

    this.scrollDateBadge ??= createSharedMediaScrollDateBadge({
      mount: (element) => this.tabsContainer.before(element),
      getAnchorTop: () => this.navScrollableContainer.getBoundingClientRect().bottom,
      getItems: () => {
        const mediaTab = this.mediaTab;
        return mediaTab.type === 'stories' ?
          mediaTab.contentTab.querySelectorAll<HTMLElement>('.stories-album-content .search-super-item[data-timestamp]') :
          mediaTab.itemsTab.children;
      }
    });
    this.scrollDateBadge.update(true);
  }

  public cleanupHTML() {
    this.scrollDateBadge?.reset();

    this.mediaTabs.forEach((tab) => {
      tab.itemsTab.replaceChildren();

      if(tab.hideOn) {
        tab.hideOn.classList.add('hide');
      }

      if(this.hideEmptyTabs) {
        // tab.menuTab.classList.add('hide');
        this.container.classList.add('hide');
        this.container.parentElement.classList.add('search-empty');
      }

      if(tab.type === 'chats') {
        return;
      }

      if(tab.inputFilter && !this.historyStorage[tab.inputFilter]) {
        const parent = tab.contentTab.parentElement;
        if(!parent.querySelector('.preloader')) {
          putPreloader(parent, true);
        }

        const empty = parent.querySelector('.content-empty');
        empty?.remove();
      }
    });

    this.searchGroupMedia.clear();
    this.scrollable.scrollPosition = 0;
  }

  private copySearchContext(newInputFilter: MyInputMessagesFilter, nextRate: number, skipSensitive: boolean) {
    const context = copy(this.searchContext) as MediaSearchContext;
    context.inputFilter = {_: newInputFilter};
    context.nextRate = nextRate;
    context.skipSensitive = skipSensitive;
    return context;
  }

  public setQuery({peerId, query, threadId, historyStorage, folderId, minDate, maxDate, chatType, communityId}: {
    peerId: PeerId,
    query?: string,
    threadId?: number,
    historyStorage?: AppSearchSuper['historyStorage'],
    folderId?: number,
    minDate?: number,
    maxDate?: number
  } & Pick<RequestHistoryOptions, 'chatType' | 'communityId'>) {
    this.searchContext = {
      peerId,
      query: query || '',
      inputFilter: {_: this.mediaTab.inputFilter},
      threadId,
      folderId,
      minDate,
      maxDate,
      chatType,
      communityId
    };

    this.historyStorage = historyStorage ?? {};

    this.cleanup();
  }

  public destroy() {
    this.cleanup();
    this.listenerSetter.removeAll();
    this.disposeNav?.();
    this.disposeNav = undefined;
    this.scrollable.destroy();
    this.swipeHandler?.removeListeners();
    this.selection?.cleanup();

    this.scrollStartCallback =
      this.onChangeTab =
      this.selectTab =
      this.searchContextMenu =
      this.swipeHandler =
      this.selection =
      undefined;
  }
}
